import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import RubricMarkdown from "./RubricMarkdown";
import remarkGfm from "remark-gfm";
import { chatWithRubricAI } from "../utils/backendApiWrapper";
import { parsePaperDiff, applyPaperChanges, allPaperDecisionsMade, mergePaperAudits, keepSoundPlaceValues, keepSoundPaperTotals, keepSoundSectionBanners, markTables } from "../utils/paperDiff";
import PaperDiffReview from "./PaperDiffReview";
import useAiStatus from "../utils/useAiStatus";
import { LOCAL_MODELS, GEMINI_MODELS, OPENAI_MODELS, isRubricModelChoice } from "../utils/localModels";
import { cleanMarkdown } from "../utils/cleanMarkdown";
import { copyToClipboard } from "../utils/clipboard";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import SaveRubricModal from "./SaveRubricModal";
import MessageBox from "./MessageBox";
import DuplicateRubricDialog from "./DuplicateRubricDialog";
import QuestionPaperPreview from "./QuestionPaperPreview";
import PaperTemplates from "./PaperTemplates";
import { extractQuestionPaper, stripQuestionPaperText, restorePaperNotes } from "../utils/questionPaper";
import { toPlainMath } from "../utils/plainMath";
import { splitRubricQuestions, replaceRubricBlocks, replaceRubricBlockAt, alignRubricPaperTotal, restoreChoiceParents } from "../utils/rubricQuestions";
import { CLOSED_WORLD_BREAKDOWN } from "../utils/closedWorldRubric";
import './RubricChatbot.css';

// ---------------------------------------------------------------------------
// Rubric per-question table validator
// ---------------------------------------------------------------------------
// The AI is instructed to emit ONE block per question (or per sub-part, using
// the teacher's own numbering — e.g. Q1.i, Q2.a):
//
//   Q.<label> - <statement> (<total> marks)
//
//   Model Answer: ...
//
//   Rubrics Breakdown:
//   | Sub-step | Marks |
//   | --- | --- |
//   | ... | N |
//
//   ==================================================
//   (equals line between questions)
//
// For each block we read the declared total from the "(N marks)" tail of the
// header and sum the Marks column of the table that follows it. Any mismatch
// surfaces in a red banner above the bubble.
//
// Returns:
//   {
//     hasTable: boolean,       // false = no Q.N headers found at all
//     ok: boolean,             // true only if every question matched exactly
//     issues: Array<{ question, expectedTotal, actualSum, rows, note? }>,
//     verified: Array<{ question, total, rows }>
//   }
function toNum(v) {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// Match "Q.1 - <statement> (10 marks)" (also "Q1", "Q.1.a", "Q1.ii", "Q2.a.i",
// em/en-dash, "mark"). The number group keeps the teacher's original numbering,
// including nested sub-parts: a leading number followed by any depth of
// ".<sub>" segments (sub = letters or digits, e.g. .i .ii .a .b .1).
// `m` flag — anchor per-line. `g` — find all.
const QUESTION_HEADER_RE = /^[ \t]*Q\.?\s*([0-9]+(?:\.[a-zA-Z0-9]+)*)\s*[-–—:]\s*(.+?)\s*\(\s*([0-9]+(?:\.[0-9]+)?)\s*marks?\s*\)\s*$/gim;

function parseTableRows(section) {
  const rows = [];
  for (const line of section.split(/\r?\n/)) {
    const t = line.trim();
    if (!t.startsWith('|') || !t.endsWith('|')) continue;
    const cells = t.split('|').slice(1, -1).map(c => c.trim());
    if (cells.length < 2) continue;
    if (cells.every(c => /^:?-+:?$/.test(c))) continue; // separator row
    rows.push(cells);
  }
  return rows;
}

function validateRubricTable(markdown) {
  const text = String(markdown || '');
  const headers = [];
  // Stateful regex — reset lastIndex on a fresh string (the literal is module-scope).
  QUESTION_HEADER_RE.lastIndex = 0;
  let m;
  while ((m = QUESTION_HEADER_RE.exec(text)) !== null) {
    headers.push({
      label: `Q${m[1]}`,
      total: parseFloat(m[3]),
      headerEnd: QUESTION_HEADER_RE.lastIndex,
      headerStart: m.index
    });
  }
  if (headers.length === 0) {
    return { hasTable: false, ok: false, issues: [], verified: [] };
  }

  const issues = [];
  const verified = [];

  for (let i = 0; i < headers.length; i++) {
    const h = headers[i];
    const sectionEnd = i + 1 < headers.length ? headers[i + 1].headerStart : text.length;
    const section = text.slice(h.headerEnd, sectionEnd);
    const rows = parseTableRows(section);

    if (rows.length === 0) {
      issues.push({
        question: h.label,
        expectedTotal: h.total,
        actualSum: 0,
        rows: [],
        note: 'No breakdown table found'
      });
      continue;
    }

    // Drop the column-label row if it doesn't have a numeric in the Marks slot.
    const dataRows = toNum(rows[0][rows[0].length - 1]) === null ? rows.slice(1) : rows;
    if (dataRows.length === 0) {
      issues.push({
        question: h.label,
        expectedTotal: h.total,
        actualSum: 0,
        rows: [],
        note: 'Breakdown table has no rows'
      });
      continue;
    }

    // Pick the rightmost column whose every row parses as a number — that's
    // the Marks column. Lets us tolerate a stray third column without
    // breaking validation.
    let marksCol = -1;
    for (let c = dataRows[0].length - 1; c >= 0; c--) {
      if (dataRows.every(r => toNum(r[c]) !== null)) {
        marksCol = c;
        break;
      }
    }
    if (marksCol === -1) {
      issues.push({
        question: h.label,
        expectedTotal: h.total,
        actualSum: 0,
        rows: [],
        note: 'Could not find a numeric Marks column'
      });
      continue;
    }

    // A row whose whole label is "Total" / "Sum" / "Grand total" is a totals
    // line. A step that merely starts with those words, such as
    // "Total mass of reactants equals total mass of products", is a real mark.
    const subRows = dataRows.filter(r => !/^\**\s*(?:(?:grand\s+)?total|sum|grand)(?:\s+marks?)?\s*\**\s*$/i.test(r[0]));
    const sum = subRows.reduce((acc, r) => acc + (toNum(r[marksCol]) || 0), 0);
    const rowsForDisplay = subRows.map(r => ({
      subStep: r[0],
      marks: toNum(r[marksCol])
    }));

    if (Math.abs(sum - h.total) < 0.001) {
      verified.push({ question: h.label, total: h.total, rows: rowsForDisplay });
    } else {
      issues.push({
        question: h.label,
        expectedTotal: h.total,
        actualSum: sum,
        rows: rowsForDisplay
      });
    }
  }

  return {
    hasTable: true,
    ok: issues.length === 0,
    issues,
    verified
  };
}

function shownRubric(text, source) {
  const plain = toPlainMath(text);
  return alignRubricPaperTotal(source ? restoreChoiceParents(plain, source) : plain);
}

function missingUnderlinedWord(text) {
  const raw = String(text || '');
  if (!/خط کشیدہ|underlined/i.test(raw)) return false;
  return !/<u>\s*[^<\s][^<]*<\/u>/i.test(raw);
}

function friendlyModelName(modelId, choice) {
  const lists = [...GEMINI_MODELS, ...OPENAI_MODELS, ...LOCAL_MODELS];
  const fromId = lists.find((m) => m.value === modelId);
  if (fromId) return fromId.label;
  if (modelId) return modelId;
  const raw = String(choice || '');
  const picked = raw.includes(':') ? raw.slice(raw.indexOf(':') + 1) : raw;
  const fromChoice = lists.find((m) => m.value === picked);
  return fromChoice ? fromChoice.label : picked;
}

function formatRubricTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function RubricMetaLine({ meta, showModel = true }) {
  if (!meta) return null;
  const when = formatRubricTime(meta.at);
  const model = showModel && meta.model ? meta.model : '';
  if (!model && !when) return null;
  return (
    <div className="rc-meta-line">
      {model
        ? (meta.updated ? 'Updated with ' : 'Created with ')
        : (meta.updated ? 'Updated' : 'Created')}
      {model ? <strong>{model}</strong> : null}
      {when ? ` · ${when}` : ''}
    </div>
  );
}

export default function RubricChatbot() {
  const navigate = useNavigate();
  const { saveRubric: onSaveRubric } = useApp();
  const { currentUser } = useAuth();
  const API_BASE_URL = window.API_BASE_URL;
  const currentUsername = currentUser?.username || '';
  const currentRole = currentUser?.role || '';
  const showModelName = currentRole !== 'teacher';
  const isPrivilegedRole = ['administrator', 'examiner', 'management'].includes(currentRole);
  const isExaminer = currentRole === 'examiner';
  const canEditAny = currentRole === 'administrator';
  const [rubricModelChoice, setRubricModelChoice] = useState(() => {
    try {
      const saved = localStorage.getItem('examinerRubricModel');
      if (saved === 'openai') return 'openai:gpt-5.4';
      if (isRubricModelChoice(saved)) return saved;
    } catch (_) { /* ignore */ }
    return 'local:ggml';
  });
  React.useEffect(() => {
    if (!isExaminer) return;
    try { localStorage.setItem('examinerRubricModel', rubricModelChoice); } catch (_) { /* ignore */ }
  }, [isExaminer, rubricModelChoice]);
  const [rubricHost, setRubricHost] = useState(() => {
    try {
      const saved = localStorage.getItem('examinerRubricHost');
      if (saved === 'gpu' || saved === 'mac') return saved;
    } catch (_) { /* ignore */ }
    return 'gpu';
  });
  React.useEffect(() => {
    if (!isExaminer) return;
    try { localStorage.setItem('examinerRubricHost', rubricHost); } catch (_) { /* ignore */ }
  }, [isExaminer, rubricHost]);
  const aiStatus = useAiStatus(8000);
  const rubricHosts = (Array.isArray(aiStatus?.local?.hosts) ? aiStatus.local.hosts : [])
    .filter((h) => h && h.key && h.key !== 'default');
  const localRubric = rubricModelChoice.startsWith('local:');
  const rubricHostChoice = localRubric ? rubricHost : '';
  // Saved by any examiner. Teachers never see the control; their rubric calls
  // send this so the GPU queue banner knows, and the server applies its own copy.
  const [teacherRubricModel, setTeacherRubricModel] = useState('openai:gpt-5.4');
  const [teacherRubricHost, setTeacherRubricHost] = useState('gpu');
  const [teacherRubricNote, setTeacherRubricNote] = useState('');
  const [teacherRubricBusy, setTeacherRubricBusy] = useState(false);
  const teacherUsesShared = currentRole === 'teacher';
  const appliedRubricModel = isExaminer
    ? rubricModelChoice
    : (teacherUsesShared ? teacherRubricModel : '');
  const appliedRubricHost = isExaminer
    ? rubricHostChoice
    : (teacherUsesShared && String(teacherRubricModel).startsWith('local:') ? teacherRubricHost : '');
  React.useEffect(() => {
    if (!isExaminer && !teacherUsesShared) return;
    const token = localStorage.getItem('authToken');
    if (!API_BASE_URL || !token) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/ai/teacher-rubric-model`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (isRubricModelChoice(d.model)) setTeacherRubricModel(d.model);
        if (d.host === 'gpu' || d.host === 'mac' || (typeof d.host === 'string' && d.host)) {
          setTeacherRubricHost(d.host);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isExaminer, teacherUsesShared, API_BASE_URL]);
  const [pageNotice, setPageNotice] = useState('');
  const [pageNoticeOpen, setPageNoticeOpen] = useState(false);
  const [pageNoticeEditing, setPageNoticeEditing] = useState(false);
  const [pageNoticeDraft, setPageNoticeDraft] = useState('');
  const [pageNoticeBusy, setPageNoticeBusy] = useState(false);
  const [pageNoticeError, setPageNoticeError] = useState('');
  React.useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!API_BASE_URL || !token) return undefined;
    let cancelled = false;
    fetch(`${API_BASE_URL}/ai/rubric-generate-notice`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        const text = String(d.message || '');
        setPageNotice(text);
        if (text.trim()) setPageNoticeOpen(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [API_BASE_URL]);
  const openPageNotice = (editing) => {
    setPageNoticeDraft(pageNotice);
    setPageNoticeEditing(!!editing);
    setPageNoticeError('');
    setPageNoticeOpen(true);
  };
  const savePageNotice = async () => {
    if (!isExaminer || pageNoticeBusy) return;
    setPageNoticeBusy(true);
    setPageNoticeError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/ai/rubric-generate-notice`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: pageNoticeDraft })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPageNoticeError(data.message || 'Could not save the notice');
        return;
      }
      const text = String(data.message || '');
      setPageNotice(text);
      setPageNoticeEditing(false);
      setPageNoticeOpen(!!text.trim());
    } catch (_) {
      setPageNoticeError('Could not save the notice');
    } finally {
      setPageNoticeBusy(false);
    }
  };
  const saveTeacherRubric = async (model, host) => {
    setTeacherRubricModel(model);
    if (host != null) setTeacherRubricHost(host);
    if (!isExaminer) return;
    const hostToSave = model.startsWith('local:') ? (host != null ? host : teacherRubricHost) : '';
    setTeacherRubricBusy(true);
    setTeacherRubricNote('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/ai/teacher-rubric-model`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ model, host: hostToSave })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTeacherRubricNote(data.message || 'Could not save');
        return;
      }
      if (data.host) setTeacherRubricHost(data.host);
      setTeacherRubricNote('Saved');
      setTimeout(() => setTeacherRubricNote(''), 2000);
    } catch (_) {
      setTeacherRubricNote('Could not save');
    } finally {
      setTeacherRubricBusy(false);
    }
  };
  const [leftSideOpen, setLeftSideOpen] = useState(() => {
    try { return localStorage.getItem('rubricSideLeft') !== '0'; } catch (_) { return true; }
  });
  const [rightSideOpen, setRightSideOpen] = useState(() => {
    try { return localStorage.getItem('rubricSideRight') !== '0'; } catch (_) { return true; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('rubricSideLeft', leftSideOpen ? '1' : '0'); } catch (_) { /* ignore */ }
  }, [leftSideOpen]);
  React.useEffect(() => {
    try { localStorage.setItem('rubricSideRight', rightSideOpen ? '1' : '0'); } catch (_) { /* ignore */ }
  }, [rightSideOpen]);
  const [messages, setMessages] = useState([]);
  const [rubricHistory, setRubricHistory] = useState([]);
  const [showRubricHistory, setShowRubricHistory] = useState(false);
  const sessionIdRef = React.useRef(null);
  const historySavingRef = React.useRef(false);
  const historyDirtyRef = React.useRef(false);
  const [historyNudge, setHistoryNudge] = useState(0);
  const sourceTextRef = React.useRef('');
  const waitingRef = React.useRef(false);
  const ruleNameRef = React.useRef('');
  const [inputText, setInputText] = useState("");
  // OCR upload — multiple answer-sheet images get transcribed by the
  // backend and the text is appended to `sourceText` (the Paste Answer Key
  // textarea), which the teacher can then edit before generating a rubric.
  const [ocrUploading, setOcrUploading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(''); // e.g. "Transcribing 3 image(s)..."
  // Thumbnails of the most recent batch of images sent to the OCR endpoint.
  // Each entry holds a name + an object URL so we can render a small
  // preview grid in the upload card. URLs are revoked when the batch is
  // cleared / replaced to avoid leaking blobs.
  const [ocrPreviews, setOcrPreviews] = useState([]);
  // Helper: revoke any in-flight object URLs we created so we don't leak
  // memory across batches.
  const revokeOcrPreviews = React.useCallback((items) => {
    for (const it of (items || [])) {
      try { if (it && it.url) URL.revokeObjectURL(it.url); } catch (_) {}
    }
  }, []);
  // Cleanup on unmount.
  React.useEffect(() => {
    return () => revokeOcrPreviews(ocrPreviews);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sourceText, setSourceText] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [gpuQueue, setGpuQueue] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [waitingForRubricChoice, setWaitingForRubricChoice] = useState(false);
  const [pendingConversation, setPendingConversation] = useState(null);
  // Inline edit state for the AI's "Corrected Question Paper" bubble. The
  // teacher can tweak the AI output before clicking "Corrected Rubrics" so
  // the rubric generator works off the teacher-curated text instead of the
  // raw AI version. Index points into `messages`; null means not editing.
  const [editingPaperIdx, setEditingPaperIdx] = useState(null);
  const [paperDraft, setPaperDraft] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionDraft, setQuestionDraft] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [rubricContentToSave, setRubricContentToSave] = useState("");
  const [questionPaper, setQuestionPaper] = useState(null);
  const [pickedByIdx, setPickedByIdx] = useState({});
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  // Duplicate-rubric dialog state — see RubricUploader for the same flow.
  const [duplicateState, setDuplicateState] = useState({
    open: false, message: '', existingId: null, retryArgs: null, canCreateSecond: false, creatingSecond: false
  });

  // Subject rule sets are now stored in the backend (subject_rules table) so
  // that admin/examiner can see all teachers' rules and teachers only see
  // their own. Each entry is { id, subject_key, display_name, rules,
  // created_by, is_default }. is_default rows are admin-curated defaults
  // baked into the controller — every user sees them, nobody can edit them.
  const [ruleSets, setRuleSets] = useState([]);
  const [activeRuleId, setActiveRuleId] = useState(() => localStorage.getItem('rubricActiveRuleId') || '');
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [draftRules, setDraftRules] = useState([]);
  const [draftDisplayName, setDraftDisplayName] = useState('');
  const [draftInstructions, setDraftInstructions] = useState('');
  const [showSaveAsNew, setShowSaveAsNew] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  // Admin-only "+ Create Rules" prompt — produces a brand-new admin-owned
  // rule set that becomes visible to all teachers (because teachers' GET
  // returns rule sets owned by any administrator).
  const [showCreateRules, setShowCreateRules] = useState(false);
  const [createRulesName, setCreateRulesName] = useState('');

  // Persist active rule id locally (just the id — the rules themselves come
  // from the API and are no longer stored client-side).
  React.useEffect(() => {
    try { localStorage.setItem('rubricActiveRuleId', activeRuleId || ''); } catch {}
  }, [activeRuleId]);

  // Load rule sets from API on mount.
  const loadRuleSets = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/subject-rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setRuleSets(data.data || []);
    } catch (err) {
      console.error('[Rubric] Failed to load subject rules:', err);
    }
  }, [API_BASE_URL]);
  React.useEffect(() => { loadRuleSets(); }, [loadRuleSets]);

  // Helpers: find a rule set by id, work out edit/delete permissions.
  const findRuleSet = (id) => ruleSets.find(rs => String(rs.id) === String(id));
  const canEditRuleSet = (rs) => {
    if (!rs) return false;
    if (rs.is_default) return false;                 // admin defaults are read-only for everyone
    if (rs.created_by === currentUsername) return true;
    return canEditAny;                                // admins can edit any non-default
  };
  const canDeleteRuleSet = (rs) => canEditRuleSet(rs);

  // Snapshot of the rules editor's contents at open time. Used to detect
  // unsaved edits when the user tries to close — JSON-stringify so the
  // deep compare reduces to string equality.
  const [editorSnapshot, setEditorSnapshot] = useState(null);

  const openRulesEditor = (id) => {
    const rs = findRuleSet(id);
    if (!rs) return;
    const initialDisplayName = rs.display_name || '';
    const initialRules = (rs.rules || []).map(r => ({ ...r }));
    const initialInstructions = rs.instructions || '';
    setEditingRuleId(String(id));
    setDraftDisplayName(initialDisplayName);
    setDraftRules(initialRules);
    setDraftInstructions(initialInstructions);
    setEditorSnapshot(JSON.stringify({
      displayName: initialDisplayName,
      rules: initialRules,
      instructions: initialInstructions
    }));
  };
  const closeRulesEditor = () => {
    setEditingRuleId(null);
    setDraftRules([]);
    setDraftDisplayName('');
    setDraftInstructions('');
    setEditorSnapshot(null);
  };

  // Detect whether the editor has unsaved changes vs. the snapshot taken
  // at open time. Read-only viewers can never produce changes (the inputs
  // are disabled), so `false` is the right answer for them too.
  const hasUnsavedRulesChanges = () => {
    if (!editorSnapshot) return false;
    const current = JSON.stringify({
      displayName: draftDisplayName,
      rules: draftRules,
      instructions: draftInstructions
    });
    return current !== editorSnapshot;
  };

  // User-triggered close (overlay click, Close button, Cancel button). Asks
  // before discarding unsaved changes; closes silently when nothing has
  // changed. Save / Save-as-New / Delete flows still call `closeRulesEditor`
  // directly because their close is intentional after a successful action.
  const requestCloseRulesEditor = () => {
    if (hasUnsavedRulesChanges()) {
      const ok = window.confirm(
        'You have unsaved changes to these marking rules. Discard them and close?'
      );
      if (!ok) return;
    }
    closeRulesEditor();
  };

  // Save changes to an editable rule set (PUT).
  const saveRulesEditor = async () => {
    if (!editingRuleId) return;
    const rs = findRuleSet(editingRuleId);
    if (!rs || !canEditRuleSet(rs)) return;
    const cleaned = draftRules
      .map(r => ({ marks: String(r.marks ?? '').trim(), rule: String(r.rule ?? '').trim() }))
      .filter(r => r.marks || r.rule);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/subject-rules/${rs.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          display_name: draftDisplayName.trim() || rs.display_name,
          rules: cleaned,
          instructions: (draftInstructions || '').trim()
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMessage({ show: true, text: data.message || 'Failed to save rules.', type: 'error' });
        return;
      }
      await loadRuleSets();
      closeRulesEditor();
    } catch (err) {
      setMessage({ show: true, text: 'Failed to save rules: ' + err.message, type: 'error' });
    }
  };

  // "Save as New": clone the current draft into a brand-new rule set owned
  // by the current user. Used when the open editor is showing an admin
  // default — defaults stay untouched, the copy is editable.
  const handleSaveAsNew = async () => {
    const raw = newSubjectName.trim();
    if (!raw) return;
    const key = raw.toLowerCase().replace(/\s+/g, '_');
    const cleaned = draftRules
      .map(r => ({ marks: String(r.marks ?? '').trim(), rule: String(r.rule ?? '').trim() }))
      .filter(r => r.marks || r.rule);
    try {
      const token = localStorage.getItem('authToken');
      const trimmedInstructions = (draftInstructions || '').trim();
      const res = await fetch(`${API_BASE_URL}/subject-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          subject_key: key,
          display_name: raw,
          rules: cleaned,
          instructions: trimmedInstructions
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ show: true, text: data.message || 'Failed to create rule set.', type: 'error' });
        return;
      }
      setShowSaveAsNew(false);
      setNewSubjectName('');
      await loadRuleSets();
      // Open the editor on the freshly-created copy so the teacher can keep editing.
      const newId = data.id;
      setEditingRuleId(String(newId));
      setDraftDisplayName(raw);
      setDraftRules(cleaned.map(r => ({ ...r })));
      setDraftInstructions(trimmedInstructions);
    } catch (err) {
      setMessage({ show: true, text: 'Failed to create rule set: ' + err.message, type: 'error' });
    }
  };

  // Delete a user-created rule set (defaults can't be removed — they live
  // in the controller and re-appear on every fetch).
  const handleDeleteSubject = async (id) => {
    const rs = findRuleSet(id);
    if (!rs || !canDeleteRuleSet(rs)) return;
    if (!window.confirm(`Delete the "${rs.display_name}" rules entirely?`)) return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/subject-rules/${rs.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMessage({ show: true, text: data.message || 'Failed to delete rule set.', type: 'error' });
        return;
      }
      if (activeRuleId === String(id)) setActiveRuleId('');
      await loadRuleSets();
      closeRulesEditor();
    } catch (err) {
      setMessage({ show: true, text: 'Failed to delete rule set: ' + err.message, type: 'error' });
    }
  };

  // Admin-only create-from-scratch flow. Owned by the current admin user;
  // every teacher sees it because the backend GET surfaces admin-owned
  // rule sets to teachers as additional shared defaults.
  const handleCreateNewAdminRuleSet = async () => {
    const raw = createRulesName.trim();
    if (!raw) return;
    const key = raw.toLowerCase().replace(/\s+/g, '_');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/subject-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ subject_key: key, display_name: raw, rules: [] })
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ show: true, text: data.message || 'Failed to create rule set.', type: 'error' });
        return;
      }
      setShowCreateRules(false);
      setCreateRulesName('');
      await loadRuleSets();
      // Drop straight into the editor on the new (empty) rule set.
      setEditingRuleId(String(data.id));
      setDraftDisplayName(raw);
      setDraftRules([]);
      setDraftInstructions('');
    } catch (err) {
      setMessage({ show: true, text: 'Failed to create rule set: ' + err.message, type: 'error' });
    }
  };

  // Currently-active rule set object (or null) — used by handleSend and the
  // disabled-state of the Generate button.
  const activeRuleSet = activeRuleId ? findRuleSet(activeRuleId) : null;
  sourceTextRef.current = sourceText;

  React.useEffect(() => {
    const cleaned = toPlainMath(sourceText);
    if (cleaned !== sourceText) setSourceText(cleaned);
  }, [sourceText]);
  waitingRef.current = waitingForRubricChoice;
  ruleNameRef.current = activeRuleSet ? (activeRuleSet.display_name || '') : '';

  const historyKey = `rubricGeneratorHistory:${currentUsername || 'local'}`;

  const loadGeneratorHistory = React.useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !currentUsername) return;
    try {
      const res = await fetch(`${API_BASE_URL}/rubrics/generator-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data.items)) setRubricHistory(data.items);
    } catch (_) { /* keep the list already on screen */ }
  }, [API_BASE_URL, currentUsername]);

  React.useEffect(() => {
    loadGeneratorHistory();
  }, [loadGeneratorHistory]);

  // A browser-only list from before this was stored on the account.
  // Upload it once, then drop the local copy.
  React.useEffect(() => {
    if (!currentUsername) return;
    let local = [];
    try {
      const raw = localStorage.getItem(historyKey);
      local = raw ? JSON.parse(raw) : [];
    } catch (_) { local = []; }
    if (!Array.isArray(local) || local.length === 0) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    (async () => {
      for (const item of local) {
        if (!item || !Array.isArray(item.messages) || item.messages.length === 0) continue;
        try {
          await fetch(`${API_BASE_URL}/rubrics/generator-history`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              ruleName: item.ruleName || '',
              preview: item.preview || '',
              hasRubric: !!item.hasRubric,
              messages: item.messages,
              sourceText: item.sourceText || '',
              waitingForRubricChoice: !!item.waitingForRubricChoice
            })
          });
        } catch (_) { /* try the rest */ }
      }
      if (cancelled) return;
      try { localStorage.removeItem(historyKey); } catch (_) { /* ignore */ }
      loadGeneratorHistory();
    })();
    return () => { cancelled = true; };
  }, [API_BASE_URL, currentUsername, historyKey, loadGeneratorHistory]);

  // Keep the open chat on the account, so logout and another computer can open it.
  React.useEffect(() => {
    if (!messages.length || !currentUsername) return;
    const timer = setTimeout(async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      if (historySavingRef.current) {
        historyDirtyRef.current = true;
        return;
      }
      historySavingRef.current = true;
      const hasRubric = messages.some((m) => m.isRubric);
      const rubricMsg = [...messages].reverse().find((m) => m.isRubric);
      const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
      const previewSource = (rubricMsg || lastAssistant || messages[messages.length - 1]).content || '';
      const preview = String(previewSource).replace(/\s+/g, ' ').trim().slice(0, 160);
      try {
        const res = await fetch(`${API_BASE_URL}/rubrics/generator-history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            id: sessionIdRef.current,
            ruleName: ruleNameRef.current || '',
            preview,
            hasRubric,
            messages,
            sourceText: sourceTextRef.current || '',
            waitingForRubricChoice: waitingRef.current
          })
        });
        const data = await res.json();
        if (!res.ok || !data.id) return;
        sessionIdRef.current = data.id;
        setRubricHistory((prev) => {
          const entry = {
            id: data.id,
            savedAt: new Date().toISOString(),
            ruleName: ruleNameRef.current || '',
            preview,
            hasRubric
          };
          return [entry, ...prev.filter((s) => String(s.id) !== String(data.id))].slice(0, 40);
        });
      } catch (_) { /* the next change tries again */ }
      finally {
        historySavingRef.current = false;
        if (historyDirtyRef.current) {
          historyDirtyRef.current = false;
          setHistoryNudge((n) => n + 1);
        }
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [API_BASE_URL, messages, currentUsername, historyNudge]);

  const openSavedRubric = async (item) => {
    const token = localStorage.getItem('authToken');
    let full = item;
    if (!Array.isArray(item.messages) && token) {
      try {
        const res = await fetch(`${API_BASE_URL}/rubrics/generator-history/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.item) full = data.item;
      } catch (_) { /* open what we already have */ }
    }
    sessionIdRef.current = full.id;
    const restored = Array.isArray(full.messages) ? full.messages : [];
    setMessages(restored);
    setWaitingForRubricChoice(!!full.waitingForRubricChoice);
    setPendingConversation(full.waitingForRubricChoice ? restored : null);
    if (full.sourceText) setSourceText(full.sourceText);
    setEditingPaperIdx(null);
    setPaperDraft('');
    setShowRubricHistory(false);
  };

  const deleteGeneratorHistory = async (event, item) => {
    event.stopPropagation();
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/rubrics/generator-history/${item.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      setRubricHistory((prev) => prev.filter((s) => String(s.id) !== String(item.id)));
      if (String(sessionIdRef.current) === String(item.id)) {
        sessionIdRef.current = null;
        setMessages([]);
        setPendingConversation(null);
        setWaitingForRubricChoice(false);
      }
    } catch (_) { /* leave the row if the delete did not reach the server */ }
  };

  // ---------------- Answer-sheet image OCR ----------------
  // Teacher uploads one or more photos of their solution; the backend OCRs
  // each image and we append the transcripts to the Paste Answer Key
  // textarea. Teacher can then edit the text before generating a rubric.
  const handleAnswerSheetImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file later
    if (files.length === 0) return;

    // Reject anything that isn't an image upfront — the endpoint only knows
    // how to OCR images.
    const images = files.filter(f => /^image\//i.test(f.type));
    const rejected = files.length - images.length;
    if (images.length === 0) {
      setMessage({
        show: true,
        text: 'Please upload image files only (JPG, PNG, etc.). For text documents, paste the content into the answer-key box directly.',
        type: 'error'
      });
      return;
    }
    if (rejected > 0) {
      setMessage({
        show: true,
        text: `Skipping ${rejected} non-image file(s); transcribing ${images.length} image(s)...`,
        type: 'info'
      });
    }

    setOcrUploading(true);
    setOcrProgress(`Transcribing ${images.length} image(s)...`);

    // Build preview thumbnails for THIS batch and replace the previous
    // batch's previews. Object URLs from the previous batch are revoked
    // first to keep memory in check.
    const newPreviews = images.map((img) => ({
      name: img.name,
      size: img.size,
      url: URL.createObjectURL(img)
    }));
    setOcrPreviews((prev) => {
      revokeOcrPreviews(prev);
      return newPreviews;
    });

    try {
      const formData = new FormData();
      for (const img of images) formData.append('files', img);

      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/ocr/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'OCR request failed');
      }

      // Stitch the transcripts into one block. Per-image headers help the
      // teacher (and the rubric AI) see where one sheet ends and the next
      // begins — and make it easy for the teacher to edit a specific page.
      const blocks = (data.transcripts || []).map(t => {
        const header = `=== ${t.image_name} ===`;
        if (t.error) return `${header}\n[OCR failed: ${t.error}]`;
        return `${header}\n${(t.text || '').trim() || '[No text extracted]'}`;
      });
      const newText = blocks.join('\n\n');

      // Append to whatever the teacher already pasted/typed, separated by a
      // blank line. Replacing would be destructive.
      setSourceText(prev => prev && prev.trim()
        ? `${prev.trimEnd()}\n\n${newText}`
        : newText);

      const failed = (data.transcripts || []).filter(t => t.error).length;
      if (failed > 0) {
        setMessage({
          show: true,
          text: `Transcribed ${images.length - failed}/${images.length} images. ${failed} failed — see [OCR failed: …] markers in the answer-key box.`,
          type: 'info'
        });
      } else {
        setMessage({
          show: true,
          text: `Transcribed ${images.length} image(s) into the answer-key box. Review and edit before generating.`,
          type: 'success'
        });
      }
    } catch (err) {
      console.error('[Rubric] OCR transcribe failed:', err);
      setMessage({ show: true, text: `OCR failed: ${err.message}`, type: 'error' });
    } finally {
      setOcrUploading(false);
      setOcrProgress('');
    }
  };

  const modelLabel = (modelId) => friendlyModelName(modelId, appliedRubricModel);

  const rubricBubble = (result, updated) => {
    if (typeof result === 'string') {
      return { role: 'assistant', content: result, isRubric: true };
    }
    return {
      role: 'assistant',
      content: result.content,
      isRubric: true,
      rubricMeta: {
        model: modelLabel(result.model),
        at: new Date().toISOString(),
        ...(updated ? { updated: true } : {})
      }
    };
  };

  // ---------------- Chat Handler ----------------
  const handleSend = async (action) => {
    if (!inputText.trim() && !sourceText.trim()) return;

    const checking = action === 'check';
    const activeRows = activeRuleSet ? (activeRuleSet.rules || []) : [];
    if (!checking && (!activeRuleSet || activeRows.length === 0)) {
      setMessage({
        show: true,
        text: 'Please select a Subject Rules set (e.g. Maths) before generating a rubric. Click ✏️ first if its rules table is empty.',
        type: 'error'
      });
      return;
    }

    const userMessage = inputText || (checking
      ? 'Check this paper for mistakes.'
      : 'Generate rubrics from the answer key in the side panel.');
    const newMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setInputText("");
    setLoading(true);
    setBusyAction(checking ? 'check' : 'rubric');

    try {
      if (!checking) {
        const rubric = await handleGenerateRubric(newMessages, false);
        setMessages([...newMessages, rubricBubble(rubric)]);
        return;
      }

      const review = await handlePaperReview(newMessages);
      const reviewRaw = review && typeof review === 'object' && review.content != null ? review.content : review;
      if (typeof reviewRaw === 'string' && reviewRaw.startsWith('Error:')) {
        throw new Error(reviewRaw.replace(/^Error:\s*/, '') || 'Paper review failed');
      }
      const reviewMeta = {
        model: modelLabel(review && review.model),
        at: new Date().toISOString()
      };
      const parsed = mergePaperAudits(sourceText, parsePaperDiff(reviewRaw, sourceText));

      if (!parsed.changes.length) {
        const note = parsed.error
          ? 'The line-by-line review could not be read. The answer key was not changed.'
          : parsed.dropped > 0
            ? `Suggested corrections could not be matched to the answer key (${parsed.dropped} dropped).`
            : 'No issues found in the answer key.';
        setMessages([...newMessages, { role: 'assistant', content: note, rubricMeta: reviewMeta }]);
      } else {
        const reviewMessage = {
          role: 'assistant',
          content: `Review ${parsed.changes.length} suggested correction${parsed.changes.length === 1 ? '' : 's'}.`,
          isPaperDiff: true,
          rubricMeta: reviewMeta,
          paperDiff: {
            sourceText,
            changes: parsed.changes,
            dropped: parsed.dropped,
            unmatched: parsed.unmatched || [],
            decisions: {},
            saved: false
          }
        };
        const updatedMessages = [...newMessages, reviewMessage];
        setMessages(updatedMessages);
        setWaitingForRubricChoice(true);
        setPendingConversation(updatedMessages);
      }
    } catch (err) {
      console.error('Error in chat:', err);
      setMessage({
        show: true,
        text: checking ? 'Error reviewing the paper. Check console for details.' : 'Error generating rubric. Check console for details.',
        type: 'error'
      });
      setMessages([
        ...newMessages,
        { role: "assistant", content: checking ? "Error reviewing the paper. Check console for details." : "Error generating rubric. Check console for details." },
      ]);
    } finally {
      setLoading(false);
      setBusyAction('');
    }
  };

  // ---------------- Line-by-line paper review ----------------
  // One model call. The reply is JSON of exact quotes from the answer key
  // plus a correction for each. The UI anchors those quotes and lets the
  // teacher accept or reject them one line at a time.
  const handlePaperReview = async (conversation) => {
    let conversationHistory = '';
    for (const msg of conversation) {
      if (msg.isPaperDiff) continue;
      conversationHistory += `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}\n\n`;
    }
    if (sourceText.trim()) {
      conversationHistory += 'Exam text/Answer key:\n' + sourceText + '\n\n';
    }

    const subjectName = activeRuleSet
      ? (activeRuleSet.display_name || activeRuleSet.subject_key || '')
      : '';
    const subjectGuidance = activeRuleSet && (activeRuleSet.instructions || '').trim()
      ? `\nSubject-specific guidance to apply (treat anything that violates this as a finding):\n${activeRuleSet.instructions.trim()}\n`
      : '';

    const reviewPrompt = `You are a strict, critical examiner reviewing a teacher's whole paper and answer key${subjectName ? ` for ${subjectName}` : ''}. Check EVERYTHING. When in doubt, flag it. Do not stay silent on a small mistake.
${subjectGuidance}
Check all of the following, and flag every instance:
- Conceptual errors (wrong idea, reasoning, cause/effect, or mechanism)
- Factual errors (wrong dates, names, definitions, figures, or values)
- Wrong formulas, equations, units, dimensions, or notation
- Spelling mistakes
- Grammar mistakes (agreement, tense, articles, word order, missing words) and punctuation that makes a sentence wrong or ambiguous
- Broken, incoherent, or incomplete sentences
- Wording that changes the meaning or leaves the answer ambiguous
- Internal contradictions (the paper disagrees with itself)
- Missing steps an answer relies on, or a question with no answer
- Translation, mixed-script, or wrong technical-term problems
- Marks counting: add the printed marks on every question once. Do not count a repeated "Question:" line or an answer line as another question. 3.5 + 3.5 = 7. A heading written as 14 × 4 = 56 already shows the total: 14 × 4 is 56, never 58, so leave 56. If that sum already equals the heading, leave the heading. Do not double it and do not change 7 into 14. If five questions of 2 sit under a heading of 10, the total is correct — do not change 10. Only change a heading when your addition gives a different number, and write that number. The paper total is the maximum a student can score. When a section has no choice, it is the sum of that section. When it says attempt any K, it is K times one question, and extra attempts stay extra. Do not raise that total to the sum of every printed question. 56 + 14 = 70 only when both sections are required. If Total Marks already equals that obtainable total, do not change it.
- Place value is counted from the right: ones, tens, hundreds, thousands, ten thousands. The last digit is ones. In 79,870 the digit 0 is in the ones place and its place value is 0. Do not change Ones to Tens. In 34,567 the digit 5 is in the hundreds place and its place value is 500.
- A section instruction is a command, not a question. On a سوال heading, "جوابات دیں؟" and "لکھیں؟" must not end with ؟. Change that ؟ to ۔. Leave ؟ on real questions (کیا، کیوں، کون).
- Question numbers: count the questions, then count the numbers. If there are 5 questions but the numbers only go i, ii, iii, iv, that is a mistake. Number them in order. The question with no number gets the missing number, and every later number moves down so the section runs i, ii, iii, iv, v. Do not leave two questions with the same number. Quote the unlabeled line, or only the wrong label (ii. → iii.).
- a) and b) under (i) are parts of (i), not the next questions. Do not rename a) to ii) or an answer line a) to iv). Leave (i), a), b), then (ii).
- A section heading that restarts numbering on purpose (for example "Short Answers (2 marks each)" with Q1, Q2, then "Long Answers (5 marks each)" with Q1, Q2 again) is NOT a sequence error. Do not renumber that paper.

Return JSON only. No markdown and no prose. Do not write a rubric or a grading scheme.

Shape:
{
  "verdict": "NO_ISSUES" or "ISSUES_FOUND",
  "changes": [
    {
      "id": "1",
      "original": "<copy the mistaken text character-for-character from the answer key>",
      "corrected": "<the same span with only the mistake fixed>",
      "reason": "<one short sentence saying what kind of mistake it is>"
    }
  ]
}

Rules:
- If the paper is genuinely clean, return {"verdict":"NO_ISSUES","changes":[]}.
- "original" MUST be an exact contiguous copy from the paper: same words, spelling, punctuation, and line breaks. One line, or a short run of lines. Never paraphrase the quote.
- Fix only the broken fragment. A grammar fix changes the wrong words and keeps the rest of the sentence. A marks fix changes the wrong number. A sequence fix changes the wrong label. Do not rewrite a sentence that is already correct, and do not reorder whole questions.
- "corrected" is that same span with only the fix applied, written in plain text. No $ signs and no LaTeX. A sum is stacked lines, for example 3.98 then + 4.39 then a line of dashes. Write 3.98 kg, not $3.98\\text{ kg}$. Write 8 ≥ 5 and 4 < 5, not $8 \\ge 5$.
- Do not include a change whose text is already correct.
- Do not invent text that is not in the paper.

Worked examples (do NOT copy this content):
- Fact: original "Water boils at 90C at one atm." / corrected "Water boils at 100C at one atm."
- Grammar: original "The mitochondria the powerhouse cell produce energy." / corrected "The mitochondria are the powerhouse of the cell and produce energy."
- Numbering: five questions where the second has no number and the rest are labeled i, ii, iii, iv. The unlabeled question becomes ii, and the later labels become iii, iv, v. The heading stays 10 نمبر because 2+2+2+2+2=10.
- Punctuation: original "مختصر سوالات کے جوابات دیں؟" / corrected "مختصر سوالات کے جوابات دیں۔" because the question mark is not needed on an instruction.
- Sequence: original "Q3. What is an atom?" when it follows Q1 with no Q2 / corrected "Q2. What is an atom?"
Unacceptable: rewriting a correct sentence into different words, or renumbering a paper whose sections intentionally restart at Q1.

` + conversationHistory;

    try {
      const extras = {
        subjectName: activeRuleSet ? (activeRuleSet.subject_key || activeRuleSet.display_name || '') : '',
        subjectRules: activeRuleSet ? (activeRuleSet.rules || []) : [],
        subjectInstructions: activeRuleSet ? (activeRuleSet.instructions || '') : '',
        mode: 'paper_diff',
        rubricModel: appliedRubricModel,
        rubricHost: appliedRubricHost,
        onQueue: setGpuQueue
      };
      const reply = await chatWithRubricAI(reviewPrompt, '', extras);
      return {
        content: reply && reply.content != null ? reply.content : reply,
        model: (reply && reply.model) || ''
      };
    } catch (error) {
      return `Error: ${error.message}`;
    }
  };

  // ---------------- Generate Rubric Call ----------------
  const handleGenerateRubric = async (conversation, useCorrected, examText) => {
    // Build conversation history. Skip the review bubble — the finalized
    // paper is passed in as the exam text, and the model must not re-correct it.
    let conversationHistory = "";
    for (const msg of conversation) {
      if (msg.isPaperDiff || msg.isConceptCheck) continue;
      conversationHistory += `${msg.role === "user" ? "User" : "Assistant"}: ${
        msg.content
      }\n\n`;
    }

    const paper = (examText != null ? examText : sourceText).trim();
    if (paper) {
      conversationHistory += "Exam text:\n" + paper + "\n\n";
    }

    // Urdu auto-detection removed — request Urdu output via the active
    // Subject Rule's Generation Instructions textbox when needed.

    // Add instruction based on user choice
    if (useCorrected) {
      conversationHistory += "\n\nUser wants: Generate rubrics with corrected answers (fix any conceptual mistakes).\n";
    } else {
      conversationHistory += "\n\nUser wants: Generate rubrics using teacher's provided answers as-is (do not correct or point out mistakes).\n";
    }

    // STRICT PER-QUESTION FORMAT — one block per question (header + underline +
    // Model Answer + per-question breakdown table). The frontend validates that
    // sum(Marks) per table == the "(N marks)" declared in that question's
    // header line. Any mismatch triggers a red banner above the bubble.
    conversationHistory += `

=== MANDATORY RUBRIC OUTPUT FORMAT ===
For EACH question (and each sub-part) in the paper, emit ONE block in EXACTLY this shape. Outside the blocks, the only allowed lines are: "Marking Guidelines:" once at the top, "Total Marks:" once, a section heading once before that section's first question, a comprehension instruction once, and one <!-- qp --> comment for given material.

QUESTION LABELS:
- The header starts with Q. plus the teacher's number. A sub-part adds the teacher's own sub-label: Q.1.i, Q.1.ii, Q.2.a. Roman numerals stay Roman, letters stay letters, digits stay digits.
- Do not flatten Q.1.1 or Q.1.2.a into Q.1, Q.2, Q.3. That renumbering happens only when someone presses Reorganize.
- A section that starts again at Q1 stays Q.1. Do not rename it Q.1.i.
- The question words stay the teacher's. Every block keeps its own "(N marks)". If the section says "(2 marks each)" and the item has no mark of its own, write (2 marks) on that item.
- Write the section heading once, on its own line, before that section's first question. Do not repeat the heading inside the question.
- If the teacher gave marks for each sub-part, use them. If the teacher gave only one total for the parent, the part headers show the split and those part totals add up to the parent total. The printed paper total does not change.

Q.<label> - <complete question statement> (<total> marks)

Model Answer: <If the user asked for the teacher's answers as-is, copy that answer exactly, including its layout. If the user asked for corrected answers, fix wrong facts and wrong working only, and leave a correct sentence unchanged.>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <what is being awarded> | <marks for this sub-step> |
| <next sub-step> | <marks> |

FORMAT RULES (strict — the frontend parses these):
- The header line MUST literally be "Q.<label> - <statement> (<N> marks)", where <label> is the teacher's EXACT question identifier (1, or a sub-part like 1.i, 2.a — see the numbering rule above). The "(N marks)" tail is REQUIRED — that's where the total comes from.
- <statement> is ONLY that question's own instruction, and it includes the number, value, or expression the student must use. If a parent heading or section instruction sits above the sub-parts (for example "حصہ اول: مختصر سوالات کے جوابات دیں" or "Short Answers: (2 marks each)"), write that heading ONCE on its own line before the first sub-part. Do NOT copy it into Q.1.i, Q.1.ii, Q.1.iii, and so on.
- QUESTION GIVEN (every subject that asks the student to use a specific number, value, or expression): write that given in the Q-line statement. The question paper and the rubric both show this line. Do not leave a bare instruction such as "Write the number in words", "Solve", "Use < or >", "Write the missing numbers", "Write in ascending order", or "Separate the improper fractions" while the value sits only in Model Answer, only in a sub-step, or only inside <!-- qp -->. Use the value from the paper. Do not invent a different one. Examples: "Q.1 - Write the number 3417 in words. (2 marks)". "Q.9 - Use < or > between 378 and 366. (2 marks)". "Q.10 - Write the missing numbers: 453, ____, 455, ____. (2 marks)". "Q.12 - Write 623, 312, 645, 345, 412 in ascending order. (5 marks)". "Q.14 - Separate the improper fractions from 2/3, 4/4, 8/9, 11/8, 16/19, 20/19. (3 marks)". "Q.15 - Write five fractions equivalent to 2/5. (5 marks)". "Q.16 - Solve 7676 − 3434. (5 marks)". A long passage, poem, or extract still goes in <!-- qp --> only. A short given (one number, one expression, one comparison, one short list, one fraction, one equation to finish) goes in the question statement and not in that comment. A recalled name, source, example, or fact is the answer, not a given. Leave that question wording as written. Rejected: "دو ذرائع حفظ کرنا اور کتابت لکھیں". Required: "کوئی سے دو ذرائع لکھیں", with حفظ کرنا and کتابت only in the Model Answer.
- A comprehension parent is the same kind of heading and is REQUIRED. "سوال نمبر 7۔ درج ذیل پیراگراف کو پڑھ کر نیچے دیے گئے سوالات کے جوابات دیجیے۔" and "Read the following passage and answer the questions." are written ONCE, as their own plain line, immediately before the shared source. They are part of the question paper. Do not drop them. Do not fold them into a sub-question. Do not put them inside the comment.
- MATERIAL THE STUDENT MUST USE is part of the question paper. A long passage, poem, or extract still goes in exactly one HTML comment, and nowhere else as normal rubric text. A short given (a number, a value, an expression, a comparison, a short list, a fraction, or an equation to finish) is written in the question statement instead, as in QUESTION GIVEN above. If the question says "the following", "below", "complete", "balance", "read", or "match" and the material is a long passage, copy that passage verbatim into the comment:
<!-- qp
<one line per line. Copy the passage as written. The only edit is <u> around a word the paper already marks as خط کشیدہ, for example <u>حجاز</u>. Do not invent an underline. The meaning stays in Model Answer. A short equation the student must finish goes in the question statement, not in this comment. The finished equation stays in Model Answer only.>
-->
A part that belongs to ONE question goes after the blank line under that question's header and before "Model Answer:".
A part shared by several sub-questions is NEVER inside the first sub-question. Write the parent instruction, then ONE comment, then the parts, in this order:
<parent instruction, once, as its own plain line>
<!-- qp
<the shared paragraph, poem, extract, table, or other given material, verbatim>
-->
Q.7.i - <only part i> (2 marks)

Model Answer: <only part i's answer>

==================================================

Q.7.ii - <only part ii> (2 marks)

Model Answer: <only part ii's answer>
The same order for English: the "Read the following passage and answer the questions." line, then one comment, then Q.1.i, Q.1.ii, and so on. Do not repeat the given material in Model Answer. Model Answer stays only the teacher's جواب (تشریح، حوالہ، معانی، or the finished solution). A question that only asks the student to write or state something, with nothing printed for them to work from, has no comment.
- Leave one blank line under the header, then "Model Answer:".
- A line of 50 equals signs sits BETWEEN questions, after that question's table and before the next header. It does not sit under the header.
- "Model Answer:" is REQUIRED. As-is copies the teacher's answer. Corrected fixes wrong facts and wrong working only.
- "Rubrics Breakdown:" is REQUIRED, followed by the 2-column markdown table. Plain text applies to maths only. The breakdown is always this table.
- The table has EXACTLY 2 columns: "Sub-step" and "Marks".
- "Total Marks:" is the maximum a student can score, not the sum of every printed question. Read "Attempt any K" and "any K questions". A parent with N parts of M marks and "any K" is worth K × M. Five parts of 2 under "any 3" are worth 6, not 10. Extra attempts are flagged as extra.
- Copy that parent line once, as its own plain line, immediately before its first sub-part. "Q.1 Write short answers to any 3 questions. (each question carries 2 marks)" stays above Q.1.i. It is not a question block and it has no Model Answer. Do the same for Q.2 and Q.3. Do not drop it and do not fold it into Q.1.i.
- "Marks" is a single positive number per row, at most 1. No ranges. No "up to". No "/X".
- A single row "| Whole answer | 1 |" is allowed only when the question is worth 1 mark.
- EXACT WORD OR FORM (English and Urdu): when the question asks for one fixed word or form (plural, singular, opposite, gender, degree, verb form, contraction, a spelling word, a one-word blank; جمع، واحد، متضاد، مذکر، مؤنث، مصدر), the Sub-step cell is that word, not a description. Model Answer is the same word. Write "| Knives | 1 |", not "| Correct plural form written | 1 |". One word is one row. A list of them is one row per word. Sentences, translations, and paragraphs stay descriptive rows.
- WORKED STEPS (every subject): each breakdown row is working the student must write. Do not give a row for copying a number already in the question, such as "| Identifies length as 200 cm | 1 |". An intermediate value is a row only inside the correct method. For length 200 cm and width 150 cm, find the perimeter, write: "| Writes P = 2(l + w) | 1 |", "| Substitutes P = 2(200 + 150) | 1 |", "| Adds inside the brackets, 200 + 150 = 350 | 1 |", "| Multiplies 2 × 350 = 700 | 1 |", "| Writes 700 cm | 1 |". Do not write "| Adds 200 + 150 = 350 correctly | 1 |" as a step that stands alone.
${CLOSED_WORLD_BREAKDOWN}
- Do NOT add a "Total" row to the table — the total is already declared in the header.
- Do NOT combine multiple questions into one table. Each question gets its own block + its own table.

MATH NOTATION — PLAIN TEXT A TEACHER OR STUDENT CAN READ:
- Do NOT use LaTeX or dollar-sign math. No \\frac, \\div, \\times, \\sqrt, ^{ } or _{ }.
- Write a fraction as 3/4. Write a mixed number as 5 1/4, with a space between the whole number and the fraction.
- Use ÷ × √ ≤ ≥ ±. Write a power as x² or x^2. Write a square root as √16 or √(x + 1).
- Keep a worked solution on a readable line, for example: 5 1/4 ÷ 1/7 = 21/4 × 7/1 = 147/4 = 36 3/4
- Stack an addition or subtraction. Write the first number, then the sign and the second number on the next line, then a line of dashes, then the result:
700,124
+ 176,923
-------
877,047
- Long division is the bracket stack, one step on each line. Never the crossed one-line method. Do not write "23 ÷ 12 = 1 (remainder 11) Bring down 4 → 114 ÷ 12 = 9". Write this instead:
        195 <-- Quotient
12 ) 2345
      - 12 (12 × 1 = 12)
      ----
       114 (Bring down 4)
     - 108 (12 × 9 = 108)
     -----
        65 (Bring down 5)
      - 60 (12 × 5 = 60)
      ----
         5 <-- Remainder
- If the teacher's answer key is already plain text, copy that wording. Do not translate it into LaTeX.

HARD ARITHMETIC RULE (validated by the frontend — mismatches are shown in red):
- For every question worth N marks, the Rubrics Breakdown has at least N rows, each row is 1 mark or less, and the rows sum to N. Equal rows are allowed when the steps are equal. Extra rows are splits of that question's Model Answer, never new explanations.
- If the sub-step marks do not add up to the total, redistribute them so they sum exactly, with no row above 1 mark. Do not change the header total. The sub-steps fit inside it.

MATH — HCF / LCM BY DIVISION METHOD (when such questions appear):
- Do NOT create order-locked sub-steps such as "Correct second division by 2" then "Correct division by 3" that force the sample's divisor sequence.
- Prefer outcome-based rows, e.g.:
  | Valid division method used (any correct divisor order) | … |
  | Arithmetic of divisions correct | … |
  | Final HCF / LCM correct | … |
- Keep the teacher's sample working in Model Answer. Put "Any valid divisor order is acceptable" in a Sub-step row, not in Model Answer.
- A different valid order from the sample is not a wrong method.

Example output for a 2-question paper:

Q.1 - Define photosynthesis and write its balanced chemical equation. (2 marks)


Model Answer: Photosynthesis is the process by which green plants and some other organisms use sunlight to synthesize foods from carbon dioxide and water. The balanced equation is 6CO2 + 6H2O → C6H12O6 + 6O2.

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| Uses sunlight to synthesize foods from carbon dioxide and water | 1 |
| Equation 6CO2 + 6H2O → C6H12O6 + 6O2 | 1 |

==================================================

Q.2 - State Ohm's law. (2 marks)


Model Answer: The current flowing through a conductor is directly proportional to the potential difference across its ends, provided temperature remains constant. V = IR.

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| directly proportional to the potential difference | 0.5 |
| temperature remains constant | 0.5 |
| V = IR | 1 |

Example output for a paper WITH SUB-PARTS — the teacher wrote:
  Q1) Short answers:   i. Define an atom (1)      ii. Define a molecule (1)
  Q2) Long answers:    a. Explain ionic bonding (3)   b. Explain covalent bonding (3)
Note how each sub-part becomes its own block, keeping the teacher's labels (1.i, 1.ii, 2.a, 2.b) — NOT renumbered to 1, 2, 3, 4:

Q.1.i - Define an atom. (1 marks)


Model Answer: <the teacher's exact answer for part i>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| Correct definition of an atom | 1 |

==================================================

Q.1.ii - Define a molecule. (1 marks)


Model Answer: <the teacher's exact answer for part ii>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| Correct definition of a molecule | 1 |

==================================================

Q.2.a - Explain ionic bonding. (3 marks)


Model Answer: <the teacher's exact answer for part a>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <first clause written in that Model Answer> | 1 |
| <second clause written in that Model Answer> | 1 |
| <third clause written in that Model Answer> | 1 |

==================================================

Q.2.b - Explain covalent bonding. (3 marks)


Model Answer: <the teacher's exact answer for part b>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <first clause written in that Model Answer> | 1 |
| <second clause written in that Model Answer> | 1 |
| <third clause written in that Model Answer> | 1 |

Example output for a paper WITH SECTION HEADINGS AND RESTARTING NUMBERS — the teacher wrote:
  Short Answers: (2 marks each)
    Q1. Define an atom
    Q2. Define a molecule
  Long Answers: (5 marks each)
    Q1. Explain ionic bonding
    Q2. Explain covalent bonding
Keep Q1 and Q2 in both sections. Do not rename them Q.1.i or Q.2.i. Each block carries its own (N marks): 2 in Short Answers, 5 in Long Answers. The section heading is written once and is not repeated inside the question:

Short Answers: (2 marks each)

Q.1 - Define an atom. (2 marks)


Model Answer: <the teacher's exact answer>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <first clause written in that Model Answer> | 1 |
| <second clause written in that Model Answer> | 1 |

==================================================

Q.2 - Define a molecule. (2 marks)


Model Answer: <the teacher's exact answer>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <first clause written in that Model Answer> | 1 |
| <second clause written in that Model Answer> | 1 |

==================================================

Long Answers: (5 marks each)

Q.1 - Explain ionic bonding. (5 marks)


Model Answer: <the teacher's exact answer>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <clause written in that Model Answer> | 1 |
| <next clause written in that Model Answer> | 1 |
| <next clause written in that Model Answer> | 1 |
| <next clause written in that Model Answer> | 1 |
| <split a compound already in that Model Answer if it has fewer than 5 sentences> | 1 |

==================================================

Q.2 - Explain covalent bonding. (5 marks)


Model Answer: <the teacher's exact answer>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <clause written in that Model Answer> | 1 |
| <next clause written in that Model Answer> | 1 |
| <next clause written in that Model Answer> | 1 |
| <next clause written in that Model Answer> | 1 |
| <split a compound already in that Model Answer if it has fewer than 5 sentences> | 1 |
`;

    try {
      const extras = activeRuleSet
        ? {
            subjectName: activeRuleSet.subject_key || activeRuleSet.display_name || '',
            subjectRules: activeRuleSet.rules || [],
            subjectInstructions: activeRuleSet.instructions || '',
            mode: 'rubric',
            rubricModel: appliedRubricModel,
            rubricHost: appliedRubricHost,
            onQueue: setGpuQueue
          }
        : { mode: 'rubric', rubricModel: appliedRubricModel, rubricHost: appliedRubricHost, onQueue: setGpuQueue };
      const reply = await chatWithRubricAI(conversationHistory, "", extras);
      return {
        content: shownRubric(reply && reply.content != null ? reply.content : reply, paper),
        model: (reply && reply.model) || ''
      };
    } catch (error) {

      return `Error: ${error.message}`;
    }
  };

  const togglePickedQuestion = (idx, id) => {
    setPickedByIdx((prev) => {
      const cur = { ...(prev[idx] || {}) };
      cur[id] = !cur[id];
      return { ...prev, [idx]: cur };
    });
  };

  const handleReorganize = async (idx) => {
    const msg = messages[idx];
    if (!msg || !msg.isRubric || loading) return;
    const paper = shownRubric(msg.content, sourceText);
    setLoading(true);
    setBusyAction('reorganize');
    try {
      const prompt = `Reorganize this exam paper. Apply every rule in your instructions. Return only the reorganized document.

PAPER:
${paper}`;
      const reply = await chatWithRubricAI(prompt, '', {
        mode: 'reorganize',
        rubricModel: appliedRubricModel,
        rubricHost: appliedRubricHost,
        onQueue: setGpuQueue
      });
      const raw = reply && reply.content != null ? reply.content : reply;
      if (typeof raw === 'string' && raw.startsWith('Error:')) {
        throw new Error(raw.replace(/^Error:\s*/, '') || 'Could not reorganize the paper.');
      }
      setMessages((prev) => [...prev, rubricBubble({
        content: shownRubric(raw, sourceText),
        model: reply && reply.model
      })]);
    } catch (err) {
      setMessage({ show: true, text: err.message || 'Could not reorganize the paper.', type: 'error' });
    } finally {
      setLoading(false);
      setBusyAction('');
    }
  };

  const regenerateSelected = async (idx) => {
    const msg = messages[idx];
    if (!msg || !msg.isRubric || loading) return;
    const picked = Object.keys(pickedByIdx[idx] || {}).filter((id) => pickedByIdx[idx][id]);
    const chosen = splitRubricQuestions(msg.content).blocks.filter((b) => picked.includes(b.id));
    if (!chosen.length) return;
    const activeRows = activeRuleSet ? (activeRuleSet.rules || []) : [];
    if (!activeRuleSet || activeRows.length === 0) {
      setMessage({
        show: true,
        text: 'Select a Subject Rules set before regenerating.',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    setBusyAction('regen');
    try {
      const prompt = `Rewrite ONLY these rubric questions: ${chosen.map((b) => b.display).join(', ')}.
Return one block per listed question and nothing else. Do not include any other question.
Keep each question's label and its "(N marks)" total exactly as they are now.
Write maths in plain text. No LaTeX and no dollar signs. Write 8 ≥ 5 and 4 < 5, not $8 \\ge 5$. Stack an addition or subtraction: the first number, then + or − and the second number, then a line of dashes, then the result. Long division is the bracket stack (12 ) 2345, quotient above, each subtraction on its own line). Never the one-line crossed method with Bring down arrows.

Each block MUST use this shape:

Q.<label> - <question statement> (<N> marks)


Model Answer: <the teacher's answer in plain text>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <step> | <marks> |

The Marks column of each block must add up to that question's "(N marks)".
The question statement must include the number, value, or expression the student uses, so the paper and the rubric both show it. Write "Write the number 3417 in words" and "Solve 7676 − 3434", not a bare "Write the number in words" or "Solve". Use the value already in the question. Do not invent a different one. A long passage stays out of the statement. Copy the teacher's question wording. Add a number or expression only when the student must work on that given, as in "Solve 7676 − 3434". A recalled name, source, example, or fact stays in the Model Answer. Rejected: "دو ذرائع حفظ کرنا اور کتابت لکھیں". Required: "کوئی سے دو ذرائع لکھیں", with حفظ کرنا and کتابت only in the Model Answer.
Each breakdown row is working the student must write. Do not award a row for copying a number already in the question. An intermediate value is a row only inside the correct method: "Substitutes P = 2(200 + 150)" and "Multiplies 2 × 350 = 700", not "Identifies length as 200 cm" and not a standalone "Adds 200 + 150 = 350 correctly".
${CLOSED_WORLD_BREAKDOWN}

Current version to replace:
${chosen.map((b) => b.text).join('\n\n')}

Answer key:
${sourceText.trim() || '(none pasted)'}
`;
      const reply = await chatWithRubricAI(prompt, '', {
        subjectName: activeRuleSet.subject_key || activeRuleSet.display_name || '',
        subjectRules: activeRows,
        subjectInstructions: activeRuleSet.instructions || '',
        mode: 'rubric',
        rubricModel: appliedRubricModel,
        rubricHost: appliedRubricHost,
        onQueue: setGpuQueue
      });
      const raw = reply && reply.content != null ? reply.content : reply;
      if (typeof raw === 'string' && raw.startsWith('Error:')) throw new Error(raw.replace(/^Error:\s*/, ''));
      const fresh = splitRubricQuestions(shownRubric(raw));
      const map = new Map();
      fresh.blocks.forEach((b) => map.set(b.id, b.text));
      if ([...map.keys()].length === 0 && fresh.blocks.length === chosen.length) {
        chosen.forEach((b, i) => map.set(b.id, fresh.blocks[i].text));
      } else {
        chosen.forEach((b, i) => {
          if (!map.has(b.id) && fresh.blocks.length === chosen.length && fresh.blocks[i]) {
            map.set(b.id, fresh.blocks[i].text);
          }
        });
      }
      if (map.size === 0) throw new Error('The new reply did not include the selected questions.');
      const stillMissing = chosen.filter((b) => !map.has(b.id));
      setMessages((prev) => prev.map((m, i) => (i === idx ? {
        ...m,
        content: shownRubric(replaceRubricBlocks(m.content, map), sourceText),
        rubricMeta: {
          model: modelLabel(reply && reply.model),
          at: new Date().toISOString(),
          updated: true
        }
      } : m)));
      setPickedByIdx((prev) => ({ ...prev, [idx]: {} }));
      if (stillMissing.length) {
        setMessage({
          show: true,
          text: `Regenerated ${map.size} question${map.size === 1 ? '' : 's'}. ${stillMissing.map((b) => b.display).join(', ')} stayed as they were.`,
          type: 'info'
        });
      }
    } catch (err) {
      setMessage({ show: true, text: err.message || 'Could not regenerate those questions.', type: 'error' });
    } finally {
      setLoading(false);
      setBusyAction('');
    }
  };

  // ---------------- Edit the AI's corrected paper ----------------
  // Teacher clicks Edit on the corrected-paper bubble. We swap the rendered
  // markdown for a textarea seeded with the current content. Save updates
  // both `messages` (so the bubble shows the edited version) AND
  // `pendingConversation` (so when the teacher then clicks "Corrected
  // Rubrics", the rubric generator sees the edited text and not the AI's
  // original output). Cancel just discards the draft.
  const startEditPaper = (idx, content) => {
    setEditingQuestion(null);
    setQuestionDraft('');
    setEditingPaperIdx(idx);
    setPaperDraft(content || '');
  };
  const cancelEditPaper = () => {
    setEditingPaperIdx(null);
    setPaperDraft('');
  };
  const startEditQuestion = (msgIdx, blockIndex, text) => {
    setEditingPaperIdx(null);
    setPaperDraft('');
    setEditingQuestion({ msgIdx, blockIndex });
    setQuestionDraft(text || '');
  };
  const cancelEditQuestion = () => {
    setEditingQuestion(null);
    setQuestionDraft('');
  };
  const saveEditQuestion = (msgIdx) => {
    if (!editingQuestion || editingQuestion.msgIdx !== msgIdx) return;
    const target = messages[msgIdx];
    if (!target) return;
    const originalBlock = splitRubricQuestions(target.content).blocks[editingQuestion.blockIndex];
    const replacement = restorePaperNotes(questionDraft, originalBlock ? originalBlock.text : '');
    const next = shownRubric(
      replaceRubricBlockAt(target.content, editingQuestion.blockIndex, replacement),
      sourceText
    );
    setMessages((prev) => prev.map((m, i) => (i === msgIdx ? { ...m, content: next } : m)));
    setPendingConversation((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((m, i) => (i === msgIdx ? { ...m, content: next } : m));
    });
    setEditingQuestion(null);
    setQuestionDraft('');
    setMessage({ show: true, text: 'Question saved. Copy and Save use this text.', type: 'success' });
  };
  const saveEditPaper = (idx) => {
    const target = messages[idx];
    const newContent = target && target.isRubric
      ? restorePaperNotes(paperDraft, target.content)
      : paperDraft;
    setMessages(prev => prev.map((m, i) =>
      i === idx ? { ...m, content: newContent } : m
    ));
    setPendingConversation(prev => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((m, i) => i === idx ? { ...m, content: newContent } : m);
    });
    setEditingPaperIdx(null);
    setPaperDraft('');
    setMessage({
      show: true,
      text: target && target.isRubric
        ? 'Rubric edits saved. Copy and Save use this text.'
        : 'Edits saved.',
      type: 'success'
    });
  };

  const editPaperCorrection = (idx, id, text) => {
    setMessages(prev => prev.map((m, i) => {
      if (i !== idx || !m.paperDiff || m.paperDiff.saved) return m;
      return {
        ...m,
        paperDiff: {
          ...m.paperDiff,
          changes: (m.paperDiff.changes || []).map((change) => (
            change.id === id ? { ...change, corrected: text } : change
          ))
        }
      };
    }));
  };

  const decidePaperChange = (idx, id, choice) => {
    setMessages(prev => prev.map((m, i) => {
      if (i !== idx || !m.paperDiff || m.paperDiff.saved) return m;
      const current = (m.paperDiff.decisions || {})[id] || '';
      const next = current === choice ? '' : choice;
      return {
        ...m,
        paperDiff: {
          ...m.paperDiff,
          decisions: { ...(m.paperDiff.decisions || {}), [id]: next }
        }
      };
    }));
  };

  const visiblePaperChanges = (diff) => (
    diff ? keepSoundSectionBanners(diff.sourceText, keepSoundPaperTotals(diff.sourceText, keepSoundPlaceValues(diff.sourceText, diff.changes))) : []
  );

  const handleSaveReviewedPaper = (idx) => {
    const diff = messages[idx] && messages[idx].paperDiff;
    const shown = visiblePaperChanges(diff);
    if (!diff || !allPaperDecisionsMade(shown, diff.decisions)) return;
    const finalText = toPlainMath(applyPaperChanges(diff.sourceText, shown, diff.decisions));
    setSourceText(finalText);
    setMessages(prev => prev.map((m, i) => i === idx ? {
      ...m,
      content: finalText,
      paperDiff: { ...m.paperDiff, saved: true, finalText }
    } : m));
    setWaitingForRubricChoice(false);
    setPendingConversation(null);
    setMessage({ show: true, text: 'Paper saved in the answer-key box. Click Generate rubrics when you want rubrics from this text.', type: 'success' });
  };

  const handleSendReviewedForRubrics = async (idx) => {
    const diff = messages[idx] && messages[idx].paperDiff;
    const shown = visiblePaperChanges(diff);
    if (!diff || !allPaperDecisionsMade(shown, diff.decisions)) return;
    const finalText = toPlainMath(applyPaperChanges(diff.sourceText, shown, diff.decisions));
    setSourceText(finalText);
    setLoading(true);
    const locked = messages.map((m, i) => i === idx ? {
      ...m,
      content: finalText,
      paperDiff: { ...m.paperDiff, saved: true, finalText }
    } : m);
    try {
      const rubric = await handleGenerateRubric(
        locked.filter((m) => !m.isPaperDiff),
        false,
        finalText
      );
      setWaitingForRubricChoice(false);
      setPendingConversation(null);
      setMessages([
        ...locked,
        { role: 'user', content: 'Send for rubrics' },
        rubricBubble(rubric)
      ]);
    } catch (err) {
      console.error('Error generating rubric:', err);
      setMessage({ show: true, text: 'Error generating rubric. Check console for details.', type: 'error' });
      setWaitingForRubricChoice(true);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Handle User Choice for Rubric Type ----------------
  const handleRubricChoice = async (useCorrected) => {
    if (!pendingConversation) return;
    if (editingPaperIdx !== null) {
      setMessage({ show: true, text: 'Save or cancel your edits to the corrected paper first.', type: 'error' });
      return;
    }

    setLoading(true);
    setWaitingForRubricChoice(false);

    try {
      const rubric = await handleGenerateRubric(pendingConversation, useCorrected);

      
      const choiceMessage = useCorrected 
        ? "Generate corrected rubrics" 
        : "Generate teacher's provided rubrics";
      
      const updatedMessages = [
        ...pendingConversation,
        { role: "user", content: choiceMessage },
        rubricBubble(rubric)
      ];
      setMessages(updatedMessages);
      setPendingConversation(null);
    } catch (err) {
      console.error('Error generating rubric:', err);
      setMessage({ show: true, text: 'Error generating rubric. Check console for details.', type: 'error' });
      setMessages([
        ...pendingConversation,
        { role: "assistant", content: "Error generating rubric. Check console for details." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Handle Exit (Cancel Rubric Generation) ----------------
  const handleExit = () => {
    setWaitingForRubricChoice(false);
    setPendingConversation(null);
    const exitMessage = { role: "user", content: "Cancelled rubric generation." };
    setMessages([...messages, exitMessage]);
  };

  // ---------------- Copy Handler ----------------
  const handleCopy = (content, index) => {
    // Copy all AI text without extraction
    // Clean markdown formatting before copying
    const cleanedContent = cleanMarkdown(content);
    copyToClipboard(cleanedContent).then(ok => {
      if (ok) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
    });
  };

  // ---------------- Save Handler ----------------
  const handleSaveRubric = (content) => {
    if (!onSaveRubric) {
      setMessage({ show: true, text: 'Save function not available.', type: 'error' });
      return;
    }
    // Store content and show modal
    const cleanedContent = cleanMarkdown(content);
    setRubricContentToSave(cleanedContent);
    setShowSaveModal(true);
  };

  // ---------------- Edit Handler ----------------
  const handleEditRubric = (content) => {
    // The editor shows the rubric only. The question-paper note stays aside
    // and is put back when this text is saved.
    const cleanedContent = cleanMarkdown(content);
    localStorage.setItem("rubricPaperSource", cleanedContent);
    localStorage.setItem("rubricText", stripQuestionPaperText(cleanedContent));
    localStorage.removeItem("rubricPdfData"); // Clear PDF data
    
    // Navigate to Edit Rubrics page
    navigate('/rubrics/edit');
  };

  // Handle save from modal
  const handleSaveFromModal = async (formData) => {
    try {
      const { class: className, subject, assignmentName, paperType, validUntil, type, createdByUsername } = formData;

      // Convert date to ISO string if provided
      let validUntilISO = null;
      if (validUntil) {
        const date = new Date(validUntil);
        if (!isNaN(date.getTime())) {
          validUntilISO = date.toISOString();
        }
      }

      const metadata = {
        class: className,
        subject: subject,
        assignmentName: assignmentName || '',
        paperType: paperType || '',
        validUntil: validUntilISO,
        type,
        createdByUsername: createdByUsername || ''
      };

      // Save to localStorage
      localStorage.setItem("rubricText", rubricContentToSave);
      localStorage.setItem("rubricPdf", ""); // Clear any PDF rubric

      try {
        await onSaveRubric(rubricContentToSave, metadata);
        setShowSaveModal(false);
        setMessage({ show: true, text: "Rubric saved successfully! It's now available in Rubric and Chat tabs.", type: 'success' });
      } catch (err) {
        if (err && (err.code === 'DUPLICATE_RUBRIC' || err.code === 'CONFIRM_SECOND_RUBRIC')) {
          setShowSaveModal(false);
          setDuplicateState({
            open: true,
            message: err.message,
            existingId: err.existingId,
            retryArgs: { content: rubricContentToSave, metadata },
            canCreateSecond: err.code === 'CONFIRM_SECOND_RUBRIC',
            creatingSecond: false
          });
          return;
        }
        throw err;
      }
    } catch (err) {
      console.error('Error saving rubric:', err);
      setMessage({ show: true, text: 'Failed to save rubric. Check console for details.', type: 'error' });
    }
  };

  // Duplicate-rubric dialog handlers
  function closeDuplicateDialog() {
    setDuplicateState({ open: false, message: '', existingId: null, retryArgs: null, canCreateSecond: false, creatingSecond: false });
  }
  async function handleViewExistingDup() {
    // Copy the rubric the teacher just tried to save into the clipboard so
    // they can paste it directly into the existing rubric on the new tab.
    const content = duplicateState.retryArgs && duplicateState.retryArgs.content;
    let copied = false;
    if (content && typeof content === 'string') {
      copied = await copyToClipboard(content);
    }
    closeDuplicateDialog();
    if (copied) {
      setMessage({ show: true, text: 'New rubric content copied to clipboard. Paste it into the existing rubric in the new tab.', type: 'success' });
    }
    // Open Saved Rubrics in a new tab so the user keeps their generated
    // rubric draft visible in this tab while editing the existing one
    // alongside it.
    window.open('/rubrics/saved', '_blank', 'noopener,noreferrer');
  }

  async function handleCreateSecondDup() {
    const retry = duplicateState.retryArgs;
    if (!retry) return;
    setDuplicateState((prev) => ({ ...prev, creatingSecond: true }));
    try {
      await onSaveRubric(retry.content, { ...retry.metadata, allowSecond: true });
      closeDuplicateDialog();
      setMessage({ show: true, text: 'Second rubric saved successfully. It\'s now available in Rubric and Chat tabs.', type: 'success' });
    } catch (err) {
      if (err && (err.code === 'DUPLICATE_RUBRIC' || err.code === 'CONFIRM_SECOND_RUBRIC')) {
        setDuplicateState((prev) => ({
          ...prev,
          message: err.message,
          existingId: err.existingId || prev.existingId,
          canCreateSecond: err.code === 'CONFIRM_SECOND_RUBRIC',
          creatingSecond: false
        }));
        return;
      }
      console.error('Error saving second rubric:', err);
      closeDuplicateDialog();
      setMessage({ show: true, text: 'Failed to save rubric. Check console for details.', type: 'error' });
    }
  }

  // Block input when waiting on backend, loading, or no subject rules picked.
  const hasActiveRules = !!activeRuleSet && (activeRuleSet.rules || []).length > 0;
  const barLocked = waitingForRubricChoice || loading;
  const noPaper = !inputText.trim() && !sourceText.trim();
  const checkDisabled = barLocked || noPaper;
  const generateDisabled = barLocked || noPaper || !hasActiveRules;

  function scrollRubricEdge(idx, edge) {
    const root = document.querySelector(`[data-rubric-msg="${idx}"]`);
    if (!root) return;
    if (edge === 'first') {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const blocks = root.querySelectorAll('.rc-qblock');
    const target = blocks.length ? blocks[blocks.length - 1] : root.querySelector('[data-rubric-end]') || root;
    target.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }

  function rubricJumps(idx) {
    return (
      <span className="rc-jump">
        <button type="button" className="rc-btn rc-btn--muted" onClick={() => scrollRubricEdge(idx, 'first')} title="Go to the first question">
          <i className="fas fa-arrow-up"></i> First
        </button>
        <button type="button" className="rc-btn rc-btn--muted" onClick={() => scrollRubricEdge(idx, 'last')} title="Go to the last question">
          <i className="fas fa-arrow-down"></i> Last
        </button>
      </span>
    );
  }

  function rubricToolbar(msg, idx) {
    const show = msg.isRubric || (!msg.isConceptCheck && !msg.isCorrectedPaper && !msg.isPaperDiff && msg.role === 'assistant');
    if (!show) return null;
    if (editingPaperIdx === idx && !msg.isPaperDiff) {
      return (
        <>
          <button className="rc-btn rc-btn--success" onClick={() => saveEditPaper(idx)}>
            <i className="fas fa-check"></i> Save edits
          </button>
          <button className="rc-btn rc-btn--muted" onClick={cancelEditPaper}>
            <i className="fas fa-times"></i> Cancel
          </button>
        </>
      );
    }
    return (
      <>
        <button className={`rc-btn ${copiedIndex === idx ? 'rc-btn--copied' : 'rc-btn--muted'}`} onClick={() => handleCopy(msg.isRubric ? stripQuestionPaperText(shownRubric(msg.content, sourceText)) : msg.content, idx)}>
          <i className={copiedIndex === idx ? 'fas fa-check' : 'fas fa-copy'}></i>
          {copiedIndex === idx ? 'Copied' : 'Copy'}
        </button>
        <button
          className="rc-btn rc-btn--primary"
          onClick={() => startEditPaper(idx, msg.isRubric ? stripQuestionPaperText(shownRubric(msg.content, sourceText)) : msg.content)}
          title="Edit this output on this page"
        >
          <i className="fas fa-edit"></i> Edit
        </button>
        {msg.isRubric && (
          <>
            <button
              className="rc-btn rc-btn--success"
              onClick={() => setQuestionPaper(extractQuestionPaper(shownRubric(msg.content, sourceText)))}
              title="Extract the questions only, for a clean exam printout"
            >
              <i className="fas fa-print"></i> Question paper
            </button>
            <button
              className="rc-btn rc-btn--secondary"
              onClick={() => handleEditRubric(shownRubric(msg.content, sourceText))}
              title="Open this rubric in the full editor"
            >
              <i className="fas fa-up-right-from-square"></i> Open in editor
            </button>
          </>
        )}
        <button className="rc-btn rc-btn--secondary" onClick={() => handleSaveRubric(msg.isRubric ? shownRubric(msg.content, sourceText) : msg.content)}>
          <i className="fas fa-save"></i> Save
        </button>
        {msg.isRubric && (
          <button
            className="rc-btn rc-btn--primary"
            disabled={loading}
            onClick={() => handleReorganize(idx)}
            title="Group the paper into sections and number the questions Q.1, Q.2, Q.3"
          >
            <i className={busyAction === 'reorganize' ? 'fas fa-spinner fa-spin' : 'fas fa-layer-group'}></i>
            {busyAction === 'reorganize' ? 'Reorganizing...' : 'Reorganize'}
          </button>
        )}
        {msg.isRubric && (() => {
          const blocks = splitRubricQuestions(shownRubric(msg.content, sourceText)).blocks;
          if (!blocks.length) return null;
          const picked = pickedByIdx[idx] || {};
          const count = blocks.filter((b) => picked[b.id]).length;
          return (
            <button
              type="button"
              className="rc-btn rc-btn--primary"
              disabled={loading || count === 0}
              onClick={() => regenerateSelected(idx)}
              title={count ? `Regenerate ${count} selected question${count === 1 ? '' : 's'}` : 'Select questions to regenerate'}
            >
              <i className={busyAction === 'regen' ? 'fas fa-spinner fa-spin' : 'fas fa-rotate'}></i>
              {busyAction === 'regen' ? 'Regenerating...' : `Regenerate selected${count ? ` (${count})` : ''}`}
            </button>
          );
        })()}
      </>
    );
  }

  return (
    <>
    <div className="rc">
      {/* Left Sidebar — Subject Rules */}
      <div className={`rc-sidebar rc-sidebar--left${leftSideOpen ? '' : ' is-collapsed'}`}>
        <div className="rc-side-toggle-row">
          <button
            type="button"
            className="rc-side-toggle"
            onClick={() => setLeftSideOpen((v) => !v)}
            title={leftSideOpen ? 'Collapse this side' : 'Expand this side'}
            aria-expanded={leftSideOpen}
          >
            <i className={`fas fa-chevron-${leftSideOpen ? 'left' : 'right'}`}></i>
          </button>
        </div>
        <div className="rc-paste-card">
          <label><i className="fas fa-list-check"></i> Subject Rules</label>
          <div className="rc-rules-list">
            {ruleSets.map(rs => {
              const idStr = String(rs.id);
              const isActive = activeRuleId === idStr;
              const label = rs.display_name;
              const showCreator = isPrivilegedRole && !rs.is_default && rs.created_by !== currentUsername;
              return (
                <div
                  key={idStr}
                  onClick={() => setActiveRuleId(isActive ? '' : idStr)}
                  title={isActive ? 'Click to disable' : 'Click to apply these rules'}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveRuleId(isActive ? '' : idStr);
                    }
                  }}
                  className="rc-rule-pill"
                  style={{
                    background: isActive
                      ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
                      : 'var(--rule-pill-bg, rgba(59, 130, 246, 0.15))',
                    color: isActive ? 'white' : 'var(--rule-pill-text, #1E40AF)',
                    boxShadow: isActive ? '0 2px 8px rgba(59,130,246,0.3)' : 'none'
                  }}
                >
                  {isActive && <i className="fa-solid fa-check"></i>}
                  <span className="rc-rule-pill__label">{label}</span>
                  {rs.is_default && (
                    <span
                      className="rc-rule-pill__badge"
                      title="Admin default — read only"
                      style={{
                        fontWeight: 700,
                        background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--rule-admin-bg, rgba(245, 158, 11, 0.2))',
                        color: isActive ? 'white' : 'var(--rule-admin-text, #B45309)'
                      }}
                    >
                      ADMIN
                    </span>
                  )}
                  {showCreator && (
                    <span
                      className="rc-rule-pill__badge"
                      title={`Created by ${rs.created_by}`}
                      style={{
                        fontWeight: 600,
                        background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--rule-creator-bg, rgba(0,0,0,0.08))',
                        color: isActive ? 'white' : 'var(--rule-creator-text, #374151)'
                      }}
                    >
                      by {rs.created_by}
                    </span>
                  )}
                  <span
                    className="rc-rule-pill__action"
                    onClick={(e) => { e.stopPropagation(); openRulesEditor(idStr); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        openRulesEditor(idStr);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    title={canEditRuleSet(rs) ? `Edit ${label} rules` : `View ${label} rules (read-only)`}
                    style={{
                      background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--rule-icon-bg, rgba(59,130,246,0.18))',
                      color: isActive ? 'white' : 'var(--rule-pill-text, #1E40AF)'
                    }}
                  >
                    <i className={`fa-solid ${canEditRuleSet(rs) ? 'fa-pen' : 'fa-eye'}`} style={{ fontSize: 9 }}></i>
                  </span>
                </div>
              );
            })}

            {canEditAny && (
              <button
                type="button"
                className="rc-rules-create-btn"
                onClick={() => { setCreateRulesName(''); setShowCreateRules(true); }}
                title="Create a new shared rule set (visible to all teachers)"
              >
                <i className="fa-solid fa-plus" style={{ marginRight: 6 }}></i>
                Create Rules
              </button>
            )}
          </div>
          {activeRuleSet && (
            <div className="rc-rules-applied">
              <i className="fa-solid fa-circle-check" style={{ marginRight: 4 }}></i>
              {activeRuleSet.display_name} rules will be applied.
            </div>
          )}
        </div>
        {isExaminer && (
          <div className="rc-paste-card rc-model-card">
            <label><i className="fas fa-microchip"></i> Model</label>
            <select
              className="rc-model-select"
              value={rubricModelChoice}
              onChange={(e) => setRubricModelChoice(e.target.value)}
              title="Model used to generate rubrics on this page"
            >
              <optgroup label="Local">
                {LOCAL_MODELS.map((m) => (
                  <option key={m.value} value={`local:${m.value}`}>{m.label}</option>
                ))}
              </optgroup>
              <optgroup label="ChatGPT">
                {OPENAI_MODELS.map((m) => (
                  <option key={m.value} value={`openai:${m.value}`}>{m.label}</option>
                ))}
              </optgroup>
              <optgroup label="Gemini">
                {GEMINI_MODELS.map((m) => (
                  <option key={m.value} value={`gemini:${m.value}`}>{m.label}</option>
                ))}
              </optgroup>
            </select>
            {localRubric && (
              <>
                <label style={{ marginTop: 8 }}><i className="fas fa-server"></i> Host</label>
                <select
                  className="rc-model-select"
                  value={rubricHost}
                  onChange={(e) => setRubricHost(e.target.value)}
                  title="Which machine runs this local model"
                >
                  {(rubricHosts.length ? rubricHosts : [
                    { key: 'gpu', label: 'Spc' },
                    { key: 'mac', label: 'Mac' }
                  ]).map((h) => (
                    <option key={h.key} value={h.key}>
                      {h.label}{h.url ? ` (${String(h.url).replace(/^https?:\/\//, '')})` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        )}
        {isExaminer && (
          <div className="rc-paste-card rc-model-card">
            <label><i className="fas fa-users"></i> Model for teachers</label>
            <select
              className="rc-model-select"
              value={teacherRubricModel}
              disabled={teacherRubricBusy}
              onChange={(e) => saveTeacherRubric(e.target.value, teacherRubricHost)}
              title="Model every teacher uses when generating a rubric"
            >
              <optgroup label="Local">
                {LOCAL_MODELS.map((m) => (
                  <option key={m.value} value={`local:${m.value}`}>{m.label}</option>
                ))}
              </optgroup>
              <optgroup label="ChatGPT">
                {OPENAI_MODELS.map((m) => (
                  <option key={m.value} value={`openai:${m.value}`}>{m.label}</option>
                ))}
              </optgroup>
              <optgroup label="Gemini">
                {GEMINI_MODELS.map((m) => (
                  <option key={m.value} value={`gemini:${m.value}`}>{m.label}</option>
                ))}
              </optgroup>
            </select>
            {String(teacherRubricModel).startsWith('local:') && (
              <>
                <label style={{ marginTop: 8 }}><i className="fas fa-server"></i> Host for teachers</label>
                <select
                  className="rc-model-select"
                  value={teacherRubricHost}
                  disabled={teacherRubricBusy}
                  onChange={(e) => saveTeacherRubric(teacherRubricModel, e.target.value)}
                  title="Which machine runs the local model for teachers"
                >
                  {(rubricHosts.length ? rubricHosts : [
                    { key: 'gpu', label: 'Spc' },
                    { key: 'mac', label: 'Mac' }
                  ]).map((h) => (
                    <option key={h.key} value={h.key}>
                      {h.label}{h.url ? ` (${String(h.url).replace(/^https?:\/\//, '')})` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary, #6b7280)', lineHeight: 1.35 }}>
              {teacherRubricNote
                ? teacherRubricNote
                : 'Every teacher uses this model. Any examiner can change it.'}
            </div>
          </div>
        )}
      </div>

      {/* Main Column */}
      <div className="rc-main">
        {/* Header */}
        <div className="rc-header">
          <div className="rc-header-icon">
            <i className="fas fa-robot"></i>
          </div>
          <div>
            <h1>AI Rubric Generator</h1>
            <p>Generate comprehensive rubrics with AI assistance</p>
          </div>
          {isExaminer && (
            <button
              type="button"
              onClick={() => openPageNotice(!pageNotice.trim())}
              title="Edit the notice everyone sees when they open this page"
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid #FCD34D',
                background: '#FFFBEB',
                color: '#92400E',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              <i className="fa-solid fa-bullhorn"></i>
              Notice
            </button>
          )}
          {rubricHistory.length > 0 && (
            <div style={{ marginLeft: isExaminer ? 0 : 'auto', position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowRubricHistory((v) => !v)}
                title="Open a rubric generated earlier on this page"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: '1px solid #93C5FD',
                  background: showRubricHistory ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'white',
                  color: showRubricHistory ? 'white' : '#1E40AF',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                <i className="fa-solid fa-clock-rotate-left"></i>
                Previous ({rubricHistory.length})
              </button>
              {showRubricHistory && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    right: 0,
                    width: 340,
                    maxHeight: 360,
                    overflowY: 'auto',
                    background: 'var(--card-bg, #fff)',
                    border: '1px solid var(--border, #E5E7EB)',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    zIndex: 20,
                    padding: 6
                  }}
                >
                  {rubricHistory.map((item) => {
                    const when = item.savedAt
                      ? new Date(item.savedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : '';
                    return (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 4,
                          borderRadius: 8
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => openSavedRubric(item)}
                          style={{
                            display: 'block',
                            flex: 1,
                            minWidth: 0,
                            textAlign: 'left',
                            border: 'none',
                            background: 'transparent',
                            borderRadius: 8,
                            padding: '8px 10px',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary, #111827)' }}>
                            {item.ruleName || 'Rubric'}
                            <span style={{ fontWeight: 600, color: item.hasRubric ? '#059669' : '#D97706', marginLeft: 6 }}>
                              {item.hasRubric ? 'Rubric' : 'In progress'}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)', marginTop: 2 }}>{when}</div>
                          {item.preview && (
                            <div style={{ fontSize: 12, color: 'var(--text-secondary, #4b5563)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.preview}
                            </div>
                          )}
                        </button>
                        <button
                          type="button"
                          title="Delete this previous rubric"
                          onClick={(event) => deleteGeneratorHistory(event, item)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#B91C1C',
                            cursor: 'pointer',
                            padding: '8px 8px 0',
                            fontSize: 13
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className="rc-chat urdu-text">
          {messages.length === 0 && !loading ? (
            <div className="rc-empty">
              <div className="rc-empty-icon">
                <i className="fas fa-wand-magic-sparkles"></i>
              </div>
              <h3>Start generating rubrics</h3>
              <span>Upload a file or paste your answer key in the sidebar, then type your instruction below.</span>
              {rubricHistory.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowRubricHistory(true)}
                  style={{
                    marginTop: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  <i className="fa-solid fa-clock-rotate-left"></i>
                  View previous rubrics ({rubricHistory.length})
                </button>
              )}
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => {
                const isEditingThis = editingPaperIdx === idx && !msg.isPaperDiff;
                const isWideBubble = isEditingThis || !!msg.isPaperDiff;
                return (
                <div key={idx} className={`rc-msg rc-msg--${msg.role === 'user' ? 'user' : 'ai'}`} data-rubric-msg={msg.isRubric ? idx : undefined}>
                  <div className={`rc-avatar rc-avatar--${msg.role === 'user' ? 'user' : 'ai'}`}>
                    <i className={msg.role === 'user' ? 'fas fa-user' : 'fas fa-robot'}></i>
                  </div>
                  {/* Force the bubble to fill the chat column while the
                      teacher is editing the corrected paper, so the
                      textarea gets the full width of the available space.
                      Without this override the bubble shrinks to the
                      length of its longest line and the editor stays
                      cramped. */}
                  <div
                    className="rc-bubble"
                    style={isWideBubble ? { width: '100%', maxWidth: '100%' } : undefined}
                  >
                    <div className={`rc-bubble-inner rc-bubble-inner--${msg.role === 'user' ? 'user' : 'ai'}`}>
                      <div className="rc-bubble-label">{msg.role === 'user' ? 'You' : 'AI'}</div>
                      {msg.rubricMeta && !msg.isRubric ? <RubricMetaLine meta={msg.rubricMeta} showModel={showModelName} /> : null}
                      {msg.isPaperDiff && msg.paperDiff ? (
                        <>
                          {msg.paperDiff.saved && (
                            <div className="pd-saved-note">Saved to the answer-key box.</div>
                          )}
                          <PaperDiffReview
                            sourceText={msg.paperDiff.sourceText}
                            changes={visiblePaperChanges(msg.paperDiff)}
                            decisions={msg.paperDiff.decisions}
                            dropped={msg.paperDiff.dropped}
                            unmatched={msg.paperDiff.unmatched}
                            tables={markTables(msg.paperDiff.sourceText, msg.paperDiff.changes)}
                            locked={!!msg.paperDiff.saved || loading}
                            onDecide={(id, choice) => decidePaperChange(idx, id, choice)}
                            onEditCorrection={(id, text) => editPaperCorrection(idx, id, text)}
                          />
                        </>
                      ) : isEditingThis ? (
                        <textarea
                          value={paperDraft}
                          onChange={(e) => setPaperDraft(e.target.value)}
                          autoFocus
                          spellCheck={false}
                          style={{
                            width: '100%',
                            // Tall enough to show ~30 lines of Urdu/English
                            // without scrolling. `vh` keeps it sensible on
                            // very tall monitors too.
                            minHeight: 'min(70vh, 700px)',
                            padding: 14,
                            fontFamily: 'inherit',
                            fontSize: '0.95rem',
                            lineHeight: 1.7,
                            border: '2px solid var(--input-border, #6366F1)',
                            borderRadius: 8,
                            background: 'var(--input-bg, #ffffff)',
                            color: 'var(--text-primary, #0f172a)',
                            outline: 'none',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            whiteSpace: 'pre-wrap'
                          }}
                        />
                      ) : (
                        <>
                          {msg.isRubric && missingUnderlinedWord(shownRubric(msg.content, sourceText)) && (
                            <div style={{ color: '#b91c1c', fontWeight: 800, marginBottom: 8 }}>
                              No underlined word.
                            </div>
                          )}
                          {msg.isRubric && (() => {
                            const v = validateRubricTable(msg.content);
                            const tools = (
                              <div className="rc-verify__tools">
                                {rubricJumps(idx)}
                                <div className="rc-actions">{rubricToolbar(msg, idx)}</div>
                              </div>
                            );
                            if (!v.hasTable) {
                              return (
                                <div className="rc-verify rc-verify--warn">
                                  <div className="rc-verify__text">
                                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }}></i>
                                    Could not find a rubric table to validate. Regenerate to get the standard table format.
                                  </div>
                                  {tools}
                                </div>
                              );
                            }
                            if (v.ok) {
                              return (
                                <div className="rc-verify rc-verify--ok">
                                  <div className="rc-verify__text">
                                    <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }}></i>
                                    Rubric totals verified — every question's sub-steps sum to its total
                                    ({v.verified.length} question{v.verified.length === 1 ? '' : 's'} checked).
                                    <RubricMetaLine meta={msg.rubricMeta} showModel={showModelName} />
                                  </div>
                                  {tools}
                                </div>
                              );
                            }
                            return (
                              <div className="rc-verify rc-verify--bad">
                                <div className="rc-verify__text">
                                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                    <i className="fa-solid fa-circle-xmark" style={{ marginRight: 6 }}></i>
                                    Rubric is not correct — sub-step marks don't add up for {v.issues.length} question{v.issues.length === 1 ? '' : 's'}:
                                  </div>
                                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {v.issues.map((it, i) => (
                                      <li key={i}>
                                        <strong>{it.question}</strong>: declared total {it.expectedTotal},
                                        but sub-steps sum to {Number(it.actualSum.toFixed(3))}
                                        {' '}(diff {Number((it.actualSum - it.expectedTotal).toFixed(3))})
                                      </li>
                                    ))}
                                  </ul>
                                  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                                    Click <em>Regenerate</em> or edit the rubric so the sub-step marks add up to the question total.
                                  </div>
                                  <RubricMetaLine meta={msg.rubricMeta} showModel={showModelName} />
                                </div>
                                {tools}
                              </div>
                            );
                          })()}
                          {msg.isRubric ? (() => {
                            const { preamble, blocks } = splitRubricQuestions(shownRubric(msg.content, sourceText));
                            if (!blocks.length) {
                              return <RubricMarkdown>{shownRubric(msg.content, sourceText)}</RubricMarkdown>;
                            }
                            const picked = pickedByIdx[idx] || {};
                            return (
                              <>
                                {preamble.trim() ? (
                                  <RubricMarkdown>{preamble}</RubricMarkdown>
                                ) : null}
                                {blocks.map((b, bi) => {
                                  const editingThis = editingQuestion
                                    && editingQuestion.msgIdx === idx
                                    && editingQuestion.blockIndex === bi;
                                  return (
                                  <div key={`${b.id}-${bi}`} className={`rc-qblock${picked[b.id] ? ' is-on' : ''}${editingThis ? ' is-editing' : ''}`}>
                                    <label className="rc-qblock__check" title={`Select ${b.display}`}>
                                      <input
                                        type="checkbox"
                                        checked={!!picked[b.id]}
                                        disabled={loading || editingThis}
                                        onChange={() => togglePickedQuestion(idx, b.id)}
                                        aria-label={`Select ${b.display}`}
                                      />
                                    </label>
                                    <div className="rc-qblock__body">
                                      {editingThis ? (
                                        <>
                                          <textarea
                                            className="rc-qedit__box"
                                            value={questionDraft}
                                            onChange={(e) => setQuestionDraft(e.target.value)}
                                            autoFocus
                                            spellCheck={false}
                                            aria-label={`Edit ${b.display}`}
                                          />
                                          <div className="rc-qedit__actions">
                                            <button type="button" className="rc-btn rc-btn--success" onClick={() => saveEditQuestion(idx)}>
                                              <i className="fas fa-check"></i> Save question
                                            </button>
                                            <button type="button" className="rc-btn rc-btn--muted" onClick={cancelEditQuestion}>
                                              <i className="fas fa-times"></i> Cancel
                                            </button>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="rc-qblock__tools">
                                            <span className="rc-qblock__label">{b.display}</span>
                                            <button
                                              type="button"
                                              className="rc-qedit"
                                              disabled={loading}
                                              onClick={() => startEditQuestion(idx, bi, stripQuestionPaperText(b.text))}
                                              title={`Edit ${b.display}`}
                                            >
                                              <i className="fas fa-pen"></i> Edit
                                            </button>
                                          </div>
                                          <RubricMarkdown>{shownRubric(b.text)}</RubricMarkdown>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  );
                                })}
                              </>
                            );
                          })() : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{toPlainMath(msg.content)}</ReactMarkdown>
                          )}
                        </>
                      )}
                      {msg.isRubric && !isEditingThis ? <span className="rc-rubric-end" data-rubric-end="" /> : null}
                    </div>
                    {msg.role === 'assistant' && (
                      <div className="rc-actions">
                        {msg.isPaperDiff && msg.paperDiff && !msg.paperDiff.saved && waitingForRubricChoice && (() => {
                          const ready = allPaperDecisionsMade(visiblePaperChanges(msg.paperDiff), msg.paperDiff.decisions);
                          return (
                            <>
                              <button
                                className="rc-btn rc-btn--success"
                                disabled={!ready || loading}
                                onClick={() => handleSaveReviewedPaper(idx)}
                                title={ready ? 'Save the reviewed paper into the answer-key box' : 'Accept or reject every correction first'}
                              >
                                <i className="fas fa-save"></i> Save Paper
                              </button>
                              <button
                                className="rc-btn rc-btn--primary"
                                disabled={!ready || loading}
                                onClick={() => handleSendReviewedForRubrics(idx)}
                                title={ready ? 'Generate rubrics from the reviewed paper' : 'Accept or reject every correction first'}
                              >
                                <i className="fas fa-wand-magic-sparkles"></i> Send for Rubrics
                              </button>
                            </>
                          );
                        })()}
                        {msg.isCorrectedPaper && waitingForRubricChoice && idx === messages.length - 1 && (
                          isEditingThis ? (
                            <>
                              <button className="rc-btn rc-btn--success" onClick={() => saveEditPaper(idx)}>
                                <i className="fas fa-check"></i> Save Edits
                              </button>
                              <button className="rc-btn rc-btn--muted" onClick={cancelEditPaper}>
                                <i className="fas fa-times"></i> Cancel Edit
                              </button>
                            </>
                          ) : (
                            <>
                              <button className={`rc-btn ${copiedIndex === idx ? 'rc-btn--copied' : 'rc-btn--muted'}`} onClick={() => handleCopy(msg.content, idx)}>
                                <i className={copiedIndex === idx ? 'fas fa-check' : 'fas fa-copy'}></i>
                                {copiedIndex === idx ? 'Copied' : 'Copy Corrected Paper'}
                              </button>
                              <button
                                className="rc-btn rc-btn--secondary"
                                onClick={() => startEditPaper(idx, msg.content)}
                                title="Edit the AI's corrected paper before generating rubrics from it"
                              >
                                <i className="fas fa-pen-to-square"></i> Edit Paper
                              </button>
                              <button className="rc-btn rc-btn--primary" onClick={() => handleRubricChoice(true)}>
                                <i className="fas fa-magic"></i> Corrected Rubrics
                              </button>
                              <button className="rc-btn rc-btn--secondary" onClick={() => handleRubricChoice(false)}>
                                <i className="fas fa-chalkboard-teacher"></i> As-Is Rubrics
                              </button>
                              <button className="rc-btn rc-btn--muted" onClick={handleExit}>
                                <i className="fas fa-times"></i> Cancel
                              </button>
                            </>
                          )
                        )}
                        {rubricToolbar(msg, idx)}
                        {msg.isRubric && !isEditingThis && rubricJumps(idx)}
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
              {loading && (
                <div className="rc-typing">
                  <div className="rc-avatar rc-avatar--ai">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="rc-typing-bubble">
                    <div className="rc-typing-dot"></div>
                    <div className="rc-typing-dot" style={{ animationDelay: '0.2s' }}></div>
                    <div className="rc-typing-dot" style={{ animationDelay: '0.4s' }}></div>
                    <span className="rc-typing-text">
                      {gpuQueue?.status === 'queued' && gpuQueue?.ahead > 0
                        ? 'Waiting in the GPU queue…'
                        : gpuQueue?.status === 'running'
                          ? (busyAction === 'reorganize' ? 'Reorganizing the paper…' : 'Generating on the GPU…')
                          : (busyAction === 'reorganize' ? 'Reorganizing the paper…' : 'AI is thinking...')}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {loading && gpuQueue?.status === 'queued' && gpuQueue?.ahead > 0 && (
          <div className="rc-queue-banner" role="status">
            <i className="fas fa-clock" aria-hidden="true"></i>
            <span>{gpuQueue.message || 'Your request is queued. The GPU is busy.'}</span>
          </div>
        )}
      </div>

      {/* Right Sidebar — OCR upload + Paste */}
      <div className={`rc-sidebar rc-sidebar--right${rightSideOpen ? '' : ' is-collapsed'}`}>
        <div className="rc-side-toggle-row rc-side-toggle-row--right">
          <button
            type="button"
            className="rc-side-toggle"
            onClick={() => setRightSideOpen((v) => !v)}
            title={rightSideOpen ? 'Collapse this side' : 'Expand this side'}
            aria-expanded={rightSideOpen}
          >
            <i className={`fas fa-chevron-${rightSideOpen ? 'right' : 'left'}`}></i>
          </button>
        </div>
        <PaperTemplates />
        {/* OCR upload — multiple answer-sheet images get transcribed and the
            text is appended to the Paste Answer Key textarea below. */}
        <div className="rc-upload-card">
          <label>
            <i className="fas fa-camera"></i> Upload Answer-sheet Image(s)
          </label>
          <input
            type="file"
            className="rc-file-input"
            accept="image/*"
            multiple
            disabled={ocrUploading}
            onChange={handleAnswerSheetImages}
          />
          {ocrUploading && (
            <div className="rc-file-badge" style={{ background: 'rgba(245,158,11,0.12)', color: '#B45309' }}>
              <i className="fas fa-spinner fa-spin"></i>
              <span>{ocrProgress || 'Transcribing...'}</span>
            </div>
          )}

          {/* Thumbnails of the most recent batch sent to OCR. Persists after
              the request completes so the teacher can see which images
              produced the transcripts in the answer-key box below. */}
          {ocrPreviews.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-muted, #64748b)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 6
              }}>
                <span>
                  <i className="fas fa-images" style={{ marginRight: 6 }}></i>
                  Sent to OCR ({ocrPreviews.length})
                </span>
                <button
                  type="button"
                  onClick={() => {
                    revokeOcrPreviews(ocrPreviews);
                    setOcrPreviews([]);
                  }}
                  title="Clear preview thumbnails (does not undo OCR)"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted, #64748b)',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                    textDecoration: 'underline',
                    padding: 0
                  }}
                >
                  Clear
                </button>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                gap: 6
              }}>
                {ocrPreviews.map((p, i) => (
                  <div
                    key={`${p.name}-${i}`}
                    title={p.name}
                    style={{
                      position: 'relative',
                      aspectRatio: '1 / 1',
                      borderRadius: 6,
                      overflow: 'hidden',
                      border: '1px solid var(--border, #e5e7eb)',
                      background: 'var(--surface, #f1f5f9)'
                    }}
                  >
                    <img
                      src={p.url}
                      alt={p.name}
                      onClick={() => window.open(p.url, '_blank', 'noopener')}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        cursor: 'zoom-in'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setOcrPreviews(prev => {
                          const target = prev[i];
                          if (target && target.url) {
                            try { URL.revokeObjectURL(target.url); } catch (_) {}
                          }
                          return prev.filter((_, idx) => idx !== i);
                        });
                      }}
                      title="Remove from preview"
                      aria-label={`Remove ${p.name} from preview`}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        padding: 0
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rc-input-bar rc-input-bar--side">
          <input
            type="text"
            className="rc-input"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter' && !generateDisabled) handleSend('rubric'); }}
            placeholder="Type your instruction or question..."
            disabled={barLocked}
          />
          <div className="rc-input-actions">
            <button
              type="button"
              className="rc-send-btn rc-check-btn"
              onClick={() => handleSend('check')}
              disabled={checkDisabled}
              title="Check the question paper for mistakes"
              aria-label={busyAction === 'check' ? 'Checking' : 'Check for mistakes'}
            >
              <i className={busyAction === 'check' ? 'fas fa-spinner fa-spin' : 'fas fa-spell-check'}></i>
              <span className="rc-send-btn__label">{busyAction === 'check' ? 'Checking...' : 'Check for mistakes'}</span>
            </button>
            <button
              type="button"
              className="rc-send-btn"
              onClick={() => handleSend('rubric')}
              disabled={generateDisabled}
              title={!hasActiveRules
                ? 'Select a Subject Rules set first'
                : (busyAction === 'rubric' ? 'Generating rubrics...' : 'Generate rubrics from this paper')}
              aria-label={busyAction === 'rubric' ? 'Generating rubrics' : 'Generate rubrics'}
            >
              <i className={busyAction === 'rubric' ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'}></i>
              <span className="rc-send-btn__label">{busyAction === 'rubric' ? 'Generating...' : 'Generate rubrics'}</span>
            </button>
          </div>
        </div>

        {/* Paste Answer Key */}
        <div className="rc-paste-card">
          <label><i className="fas fa-keyboard"></i> Paste Answer Key</label>
          <textarea
            className="rc-textarea"
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            placeholder="Paste your answer key here, or upload answer-sheet images above..."
          />
        </div>
      </div>

      {/* ── Subject Rules edit modal ─────────────────────────── */}
      {editingRuleId && (() => {
        const rs = findRuleSet(editingRuleId);
        if (!rs) return null;
        const editable = canEditRuleSet(rs);
        // "Save as New" is now available to everyone — read-only viewers need
        // it to make their own editable copy, and editable users (admins,
        // owners) want it to clone an existing rule set as a starting point
        // for a new one without touching the original.
        const showSaveAsNewBtn = true;
        return (
          <div
            onClick={requestCloseRulesEditor}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--card-bg, #fff)', borderRadius: 12,
                maxWidth: 720, width: '92%', maxHeight: '88vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 10px 40px rgba(0,0,0,0.25)'
              }}
            >
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border, #e5e7eb)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                  <i className="fa-solid fa-list-check" style={{ marginRight: 8, color: '#3B82F6' }}></i>
                  {rs.display_name} Marking Rules
                  {hasUnsavedRulesChanges() && (
                    <span style={{
                      marginLeft: 10, fontSize: 11, fontWeight: 700,
                      background: '#FEF3C7', color: '#92400E',
                      padding: '2px 8px', borderRadius: 999
                    }}>
                      Unsaved changes
                    </span>
                  )}
                </h3>
                <button
                  onClick={requestCloseRulesEditor}
                  style={{
                    background: '#EF4444', color: 'white', border: 'none',
                    borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 600
                  }}
                >
                  <i className="fa-solid fa-times" style={{ marginRight: 6 }}></i>Close
                </button>
              </div>

              <div style={{ padding: '12px 20px', fontSize: 13, color: 'var(--text-secondary)' }}>
                {rs.is_default ? (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid rgba(245, 158, 11, 0.35)',
                    color: '#92400E',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12
                  }}>
                    <i className="fa-solid fa-lock"></i>
                    Admin default — read only. Click <strong>Save as New</strong> to clone &amp; edit your own copy.
                  </span>
                ) : !editable ? (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.35)',
                    color: '#1E40AF',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12
                  }}>
                    <i className="fa-solid fa-eye"></i>
                    Read-only — created by <strong>{rs.created_by}</strong>. Use <strong>Save as New</strong> to make your own editable copy.
                  </span>
                ) : (
                  <>These deductions will be applied when you click <strong>{rs.display_name}</strong> before generating a rubric.</>
                )}
              </div>

              {editable && (
                <div style={{ padding: '0 20px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Name:</label>
                  <input
                    type="text"
                    value={draftDisplayName}
                    onChange={(e) => setDraftDisplayName(e.target.value)}
                    placeholder="Display name (e.g. My Maths)"
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      border: '1px solid var(--border, #d1d5db)', borderRadius: 6,
                      fontSize: 13,
                      background: 'var(--select-bg, #fff)',
                      color: 'var(--text-primary, #111827)'
                    }}
                  />
                </div>
              )}

              <div style={{ padding: '0 20px', overflowY: 'auto', flex: 1 }}>
                {/* Generation instructions — placed at the top so the user
                    sees the rubric-shaping prose before the deduction table. */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    display: 'block', fontSize: 12, fontWeight: 700,
                    color: 'var(--text-secondary)', marginBottom: 4
                  }}>
                    <i className="fa-solid fa-pen-clip" style={{ marginRight: 6, color: '#3B82F6' }}></i>
                    Generation Instructions <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(optional)</span>
                  </label>
                  <p style={{ margin: '0 0 6px', fontSize: 11, color: 'var(--text-muted, #9ca3af)' }}>
                    How should the rubric be generated? E.g. "Always include 4 sub-steps per question", "Use Urdu for model answers", "Each question must reference the textbook chapter."
                  </p>
                  <textarea
                    value={draftInstructions}
                    readOnly={!editable}
                    onChange={(e) => editable && setDraftInstructions(e.target.value)}
                    placeholder="Type any extra rubric-shaping instructions here..."
                    rows={12}
                    style={{
                      width: '100%',
                      minHeight: 220,
                      padding: '10px 12px',
                      border: '1px solid var(--border, #d1d5db)',
                      borderRadius: 6,
                      fontSize: 13,
                      lineHeight: 1.55,
                      background: !editable ? 'var(--surface, #f3f4f6)' : 'var(--select-bg, #fff)',
                      color: 'var(--text-primary, #111827)',
                      cursor: !editable ? 'default' : 'text',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--surface, #f9fafb)' }}>
                      <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid var(--border, #e5e7eb)', width: 120 }}>Marks</th>
                      <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid var(--border, #e5e7eb)' }}>Rule</th>
                      {editable && (
                        <th style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid var(--border, #e5e7eb)', width: 56 }}></th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {draftRules.map((r, i) => {
                      const readOnly = !editable;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border, #f3f4f6)' }}>
                          <td style={{ padding: 6 }}>
                            <input
                              type="text"
                              value={r.marks}
                              readOnly={readOnly}
                              onChange={(e) => {
                                if (readOnly) return;
                                const v = e.target.value;
                                setDraftRules(prev => prev.map((row, idx) => idx === i ? { ...row, marks: v } : row));
                              }}
                              placeholder="e.g. -0.5"
                              style={{
                                width: '100%', padding: '6px 8px',
                                border: '1px solid var(--border, #d1d5db)', borderRadius: 6,
                                fontSize: 13, fontFamily: 'monospace',
                                background: readOnly ? 'var(--surface, #f3f4f6)' : 'var(--select-bg, #fff)',
                                color: 'var(--text-primary, #111827)',
                                cursor: readOnly ? 'default' : 'text'
                              }}
                            />
                          </td>
                          <td style={{ padding: 6 }}>
                            <input
                              type="text"
                              value={r.rule}
                              readOnly={readOnly}
                              onChange={(e) => {
                                if (readOnly) return;
                                const v = e.target.value;
                                setDraftRules(prev => prev.map((row, idx) => idx === i ? { ...row, rule: v } : row));
                              }}
                              placeholder="e.g. Spelling mistake / grammar mistake"
                              style={{
                                width: '100%', padding: '6px 8px',
                                border: '1px solid var(--border, #d1d5db)', borderRadius: 6,
                                fontSize: 13,
                                background: readOnly ? 'var(--surface, #f3f4f6)' : 'var(--select-bg, #fff)',
                                color: 'var(--text-primary, #111827)',
                                cursor: readOnly ? 'default' : 'text'
                              }}
                            />
                          </td>
                          {!readOnly && (
                            <td style={{ padding: 6, textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => setDraftRules(prev => prev.filter((_, idx) => idx !== i))}
                                title="Delete row"
                                style={{
                                  background: '#FEE2E2', color: '#DC2626', border: 'none',
                                  borderRadius: 6, padding: '6px 8px', cursor: 'pointer'
                                }}
                              >
                                <i className="fa-solid fa-trash" style={{ fontSize: 12 }}></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {editable && (
                  <button
                    type="button"
                    onClick={() => setDraftRules(prev => [...prev, { marks: '', rule: '' }])}
                    style={{
                      marginTop: 10,
                      padding: '8px 14px',
                      border: '1px dashed var(--border, #cbd5e1)',
                      borderRadius: 8,
                      background: 'transparent',
                      color: '#3B82F6',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13
                    }}
                  >
                    <i className="fa-solid fa-plus" style={{ marginRight: 6 }}></i>
                    Add Row
                  </button>
                )}
              </div>

              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--border, #e5e7eb)',
                display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap'
              }}>
                <div>
                  {canDeleteRuleSet(rs) && !rs.is_default && (
                    <button
                      onClick={() => handleDeleteSubject(rs.id)}
                      title="Remove this subject entirely"
                      style={{
                        padding: '8px 14px',
                        border: '1px solid #FCA5A5',
                        borderRadius: 8,
                        background: '#FEE2E2',
                        color: '#B91C1C',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13
                      }}
                    >
                      <i className="fa-solid fa-trash" style={{ marginRight: 6 }}></i>
                      Delete Subject
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={requestCloseRulesEditor}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--border, #d1d5db)',
                      borderRadius: 8,
                      background: 'transparent',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13
                    }}
                  >
                    Cancel
                  </button>

                  {showSaveAsNewBtn && (
                    <button
                      onClick={() => { setNewSubjectName(''); setShowSaveAsNew(true); }}
                      style={{
                        padding: '8px 18px',
                        border: 'none',
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: 13,
                        boxShadow: '0 2px 8px rgba(59,130,246,0.3)'
                      }}
                    >
                      <i className="fa-solid fa-copy" style={{ marginRight: 6 }}></i>
                      Save as New
                    </button>
                  )}
                  {editable && (
                    <button
                      onClick={saveRulesEditor}
                      style={{
                        padding: '8px 18px',
                        border: 'none',
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: 13,
                        boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
                      }}
                    >
                      <i className="fa-solid fa-save" style={{ marginRight: 6 }}></i>
                      Save
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Admin Create Rules prompt ─────────────────────────── */}
      {showCreateRules && (
        <div
          onClick={() => { setShowCreateRules(false); setCreateRulesName(''); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg, #fff)', borderRadius: 12,
              padding: 24, maxWidth: 440, width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)'
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 8, fontSize: '1.05rem' }}>
              <i className="fa-solid fa-plus" style={{ marginRight: 8, color: '#3B82F6' }}></i>
              Create Shared Rule Set
            </h3>
            <p style={{ margin: 0, marginBottom: 14, fontSize: 13, color: 'var(--text-secondary)' }}>
              Enter a subject name (e.g. "History", "Computer", "Biology"). The new rule set will be owned by you and visible to every teacher as a shared default.
            </p>
            <input
              type="text"
              value={createRulesName}
              onChange={(e) => setCreateRulesName(e.target.value)}
              placeholder="Subject name..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateNewAdminRuleSet();
                if (e.key === 'Escape') { setShowCreateRules(false); setCreateRulesName(''); }
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border, #d1d5db)',
                borderRadius: 8,
                fontSize: 14,
                background: 'var(--select-bg, #fff)',
                color: 'var(--text-primary, #111827)',
                marginBottom: 16
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => { setShowCreateRules(false); setCreateRulesName(''); }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border, #d1d5db)',
                  borderRadius: 8,
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewAdminRuleSet}
                disabled={!createRulesName.trim()}
                style={{
                  padding: '8px 18px',
                  border: 'none',
                  borderRadius: 8,
                  background: !createRulesName.trim()
                    ? '#9CA3AF'
                    : 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  color: 'white',
                  cursor: !createRulesName.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: 13
                }}
              >
                <i className="fa-solid fa-plus" style={{ marginRight: 6 }}></i>
                Create &amp; Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Save as New prompt (clones admin default to editable copy) ── */}
      {showSaveAsNew && (
        <div
          onClick={() => { setShowSaveAsNew(false); setNewSubjectName(''); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg, #fff)', borderRadius: 12,
              padding: 24, maxWidth: 420, width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)'
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 8, fontSize: '1.05rem' }}>
              <i className="fa-solid fa-copy" style={{ marginRight: 8, color: '#3B82F6' }}></i>
              Save as New
            </h3>
            <p style={{ margin: 0, marginBottom: 14, fontSize: 13, color: 'var(--text-secondary)' }}>
              Give your custom copy a name (e.g. "My Maths"). You'll be able to edit the rows freely without changing the admin default.
            </p>
            <input
              type="text"
              value={newSubjectName}
              onChange={(e) => setNewSubjectName(e.target.value)}
              placeholder="New subject name..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveAsNew();
                if (e.key === 'Escape') { setShowSaveAsNew(false); setNewSubjectName(''); }
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border, #d1d5db)',
                borderRadius: 8,
                fontSize: 14,
                background: 'var(--select-bg, #fff)',
                color: 'var(--text-primary, #111827)',
                marginBottom: 16
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => { setShowSaveAsNew(false); setNewSubjectName(''); }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border, #d1d5db)',
                  borderRadius: 8,
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsNew}
                disabled={!newSubjectName.trim()}
                style={{
                  padding: '8px 18px',
                  border: 'none',
                  borderRadius: 8,
                  background: !newSubjectName.trim()
                    ? '#9CA3AF'
                    : 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  color: 'white',
                  cursor: !newSubjectName.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: 13
                }}
              >
                <i className="fa-solid fa-copy" style={{ marginRight: 6 }}></i>
                Save as New
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <SaveRubricModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveFromModal}
        currentUser={currentUser}
      />
      <DuplicateRubricDialog
        open={duplicateState.open}
        message={duplicateState.message}
        canCreateSecond={duplicateState.canCreateSecond}
        creatingSecond={duplicateState.creatingSecond}
        onCancel={closeDuplicateDialog}
        onViewExisting={handleViewExistingDup}
        onCreateSecond={handleCreateSecondDup}
      />
      <MessageBox
        message={message}
        onClose={() => setMessage({ show: false, text: '', type: 'info' })}
      />
      {pageNoticeOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 16
          }}
        >
          <div
            style={{
              background: 'white',
              padding: 28,
              borderRadius: 12,
              maxWidth: 520,
              width: '100%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              border: '3px solid #F59E0B'
            }}
          >
            <h3 style={{ margin: '0 0 14px', fontSize: 18, color: '#92400E' }}>Notice</h3>
            {pageNoticeEditing ? (
              <textarea
                value={pageNoticeDraft}
                onChange={(e) => setPageNoticeDraft(e.target.value.slice(0, 4000))}
                placeholder="Write the message everyone sees when they open Generate Rubric. Leave it empty to hide the popup."
                rows={6}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid #FCD34D',
                  fontSize: 15,
                  lineHeight: 1.45,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            ) : (
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: '#333', whiteSpace: 'pre-wrap' }}>
                {pageNotice}
              </p>
            )}
            {pageNoticeError && (
              <div style={{ marginTop: 8, color: '#B91C1C', fontSize: 13, fontWeight: 600 }}>{pageNoticeError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              {isExaminer && !pageNoticeEditing && (
                <button
                  type="button"
                  onClick={() => { setPageNoticeDraft(pageNotice); setPageNoticeEditing(true); setPageNoticeError(''); }}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 8,
                    border: '1px solid #FCD34D',
                    background: '#FFFBEB',
                    color: '#92400E',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
              )}
              {pageNoticeEditing && (
                <button
                  type="button"
                  onClick={() => { setPageNoticeEditing(false); setPageNoticeError(''); setPageNoticeOpen(!!pageNotice.trim()); }}
                  disabled={pageNoticeBusy}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    background: 'white',
                    color: '#374151',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              )}
              {pageNoticeEditing ? (
                <button
                  type="button"
                  onClick={savePageNotice}
                  disabled={pageNoticeBusy}
                  style={{
                    padding: '10px 18px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#D97706',
                    color: 'white',
                    fontWeight: 700,
                    cursor: pageNoticeBusy ? 'wait' : 'pointer'
                  }}
                >
                  {pageNoticeBusy ? 'Saving…' : 'Save'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setPageNoticeOpen(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#D97706',
                    color: 'white',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <QuestionPaperPreview paper={questionPaper} onClose={() => setQuestionPaper(null)} />
    </div>
    </>
  );
}
